<template>
    <div>
        <b-navbar class="px-2" toggleable="md" type="dark" variant="primary">
            <b-navbar-brand href="#">Appointment Reminder Settings</b-navbar-brand>
            <b-navbar-toggle class="ml-auto" target="nav_collapse3"></b-navbar-toggle>
            <b-collapse is-nav id="nav_collapse3">
            <!-- Right aligned nav items -->
            <b-navbar-nav class="ml-auto">
                <b-nav-item href="#">                    
                <button type="button" id="close-tooltip" class="btn mr-xs btn-default btn-sm" @click="closeSlideOutPanel"><i class="la la-remove"></i></button>
                <b-tooltip target="close-tooltip" triggers="hover">
                    Close
                </b-tooltip>
                </b-nav-item>                
            </b-navbar-nav>
            </b-collapse>
        </b-navbar>

        <p/>

        <PopupErrorDisplay/>

        <!-- The way between the template dropdowns and the plain message boxes. It decides by role itself: always
             there for a Super Admin, for an office user only once the area is at 'invite' / 'default'. -->
        <FollowupsUiToggle
            v-if="allAptReminderSettings"
            class="px-3"
            :target="useInlineMessages ? 'old' : 'new'"
            page-key="reminderMessages"
            :note-text="messageLayoutNote"
            @toggle="setMessageLayout" />

        <!-- One click for an office that still has wiki templates picked: runs the per-message "Switch to a plain
             message" on every one of them, one after another. Nothing is saved until Save. -->
        <div
            v-if="useInlineMessages && allAptReminderSettings && (convertJobs.length || convertBusy || convertResult)"
            class="convert-all px-3"
            data-test="convert-all">
            <div v-if="convertJobs.length" class="convert-all__row">
                <button type="button" class="convert-all__btn" data-test="convert-all-btn" :disabled="convertBusy" @click="switchEverythingToPlain">
                    {{ convertBusy ? 'Switching…' : 'Switch every message to a plain message' }}
                </button>
                <span class="convert-all__hint">{{ convertJobs.length }} {{ convertJobs.length === 1 ? 'message uses' : 'messages use' }} a wiki template. Nothing is saved until you press Save.</span>
            </div>
            <p v-if="convertResult" class="convert-all__result" role="status" data-test="convert-all-result">{{ convertSummary(convertResult) }}</p>
        </div>

        <!-- Loading indicator while settings are being fetched -->
        <div v-if="!allAptReminderSettings" class="text-center py-5">
            <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
                <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading settings...</p>
        </div>

        <b-container fluid v-if="allAptReminderSettings">            
            <b-row>
                <b-col>
                    <div class="info-section">
                        <div class="pull-right">
                            <b-button variant="success" type="submit" class="btn-sm mt-2 ml-2" @click="save">Save</b-button>
                            <b-button variant="success" type="submit" class="btn-sm mt-2 ml-2" @click="setErrored2(false);setMessage2('');$emit('closePanel', {});">Cancel</b-button>
                            <b-button v-if="aptReminderActivated" variant="danger" type="submit" class="btn-sm mt-2 ml-2" @click="showModal('pause')">Pause Appointment Reminder</b-button>
                        </div>

                        <h3 class="fw-semi-bold sec_header">General Settings for Reminders, Confirmations & Automated Recall Reminders </h3>
                        <div>                             
                            <p class="fw-semi-bold mb-1 px-2 py-1 fs-14 plan_from">Appointment Reminder Current Status: <span class="fw-semi-bold success">{{aptReminderStatus}}</span></p>
                        </div>

                        <div v-if="!aptReminderActivated">
                            <p class="fw-semi-bold mb-1 px-2 py-1 fs-14 plan_from">Start Appointment Reminder From:</p>
                            <DatePicker
                                lang="en"
                                :placeholder="datepickerText"
                                input-class="form-control mb-3"
                                v-model="activateAptReminderOn"
                                :format="dateFormatStr"
                                class="w-100"
                                >
                                <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                            </DatePicker>
                        </div>                        
                                            
                        <div class="ignore-ops">                            
                            <p class="fw-semi-bold mt-5">Ignore Appointments placed in the following Operatories:</p>
                            <multiselect
                                v-if="operatories"
                                v-model="operatoriesToIgnore"
                                :options="operatories"
                                :multiple="true"
                                :taggable="true"
                                :show-labels="false"                                    
                                label="PatientOperatory"
                                track-by="OperatoryNum"
                                aria-placeholder="Select Operatories"
                            >
                            </multiselect>
                        </div>
                        <div class="conf-apt-status mt-4 mb-4" >
                            <p class="fw-semi-bold">Don’t send confirmation text/email to following Appointment Confirmation Statuses in Open Dental:</p>
                            <multiselect
                                v-if="doNotSendAptConfirmationStatuses"
                                v-model="confirmationStatusToIgnore"
                                :options="doNotSendAptConfirmationStatuses"
                                :multiple="true"
                                :taggable="true"
                                :show-labels="false"                                    
                                label="ClientSystemFollowupStatus"
                                track-by="ODDefNum"
                                aria-placeholder="Select Statuses"
                            >
                            </multiselect>
                        </div>
                        <div class="first-msg-od-status mt-4 mb-4">
                            <b-form-checkbox
                                v-model="firstMsgOdStatusEnabled"
                                value="TRUE"
                                unchecked-value="FALSE"
                                switch
                                size="lg"
                                class="mb-2"
                            >
                                <span class="fw-semi-bold">Set First reminder / confirmation status in Open Dental for Patient</span>
                            </b-form-checkbox>
                            <p class="grey-text">When the first confirmation / reminder message is emailed and or texted to the patient, enabling this option will change the appointment status in Open Dental for the appointment to "Teamio Texted" or "Teamio Emailed".</p>

                            <div v-if="firstMsgOdStatusEnabled === 'TRUE'" class="first-msg-od-status-options mt-4">
                                <div class="conf-template-panel mt-4">
                                    <p class="fw-semi-bold">SMS: Select Confirmation Status from below for when the first SMS reminder / confirmation message goes to the patient.</p>
                                    <multiselect
                                        v-if="doNotSendAptConfirmationStatuses"
                                        v-model="firstMsgOdStatusSms"
                                        :options="doNotSendAptConfirmationStatuses"
                                        :multiple="false"
                                        :taggable="true"
                                        :show-labels="false"
                                        label="ClientSystemFollowupStatus"
                                        track-by="ODDefNum"
                                        aria-placeholder="Select Status"
                                    >
                                    </multiselect>
                                </div>
                                <div class="conf-template-panel mt-4">
                                    <p class="fw-semi-bold">Email: Select Confirmation Status from below for when the first Email reminder / confirmation message goes to the patient.</p>
                                    <multiselect
                                        v-if="doNotSendAptConfirmationStatuses"
                                        v-model="firstMsgOdStatusEmail"
                                        :options="doNotSendAptConfirmationStatuses"
                                        :multiple="false"
                                        :taggable="true"
                                        :show-labels="false"
                                        label="ClientSystemFollowupStatus"
                                        track-by="ODDefNum"
                                        aria-placeholder="Select Status"
                                    >
                                    </multiselect>
                                </div>
                            </div>
                        </div>
                            <p class="fw-semi-bold">All patients by default are opted-in to text, unless Text OK status is set to No.</p>
                            <div class="minor-panel">                            
                                <p class="fw-semi-bold">A Minor is a person under age of:</p>
                                <div>
                                    <select class="custom-select" v-model="minorAge">
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>                                    
                                    </select>                             
                                </div>
                            </div>

                            <!-- Spanish Translation Settings -->
                            <div class="spanish-settings-panel mt-4">
                                <div class="spanish-header">
                                    <span class="spanish-icon">🌐</span>
                                    <p class="fw-semi-bold mb-0">Spanish Language Support</p>
                                </div>
                                <div class="spanish-content">
                                    <b-form-checkbox
                                        v-model="spanishEnabled"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                        switch
                                        size="lg"
                                        class="mb-2"
                                    >
                                        Send messages in Spanish to Spanish-speaking patients
                                    </b-form-checkbox>
                                    <p class="text-muted small mb-0" v-if="spanishEnabled === 'TRUE'">
                                        <i class="fa fa-info-circle"></i>
                                        Patients (or their guardian for minors) with Language="spa" in Open Dental 
                                        will automatically receive appointment reminders translated to Spanish.
                                        <span v-if="useInlineMessages" data-test="spanish-note-plain">Plain messages are translated automatically when you save; wiki templates use the translations in the Wiki editor.</span>
                                        <span v-else data-test="spanish-note-wiki">Make sure your templates have Spanish translations enabled in the Wiki editor.</span>
                                    </p>
                                </div>
                            </div>

                            <!-- How your providers appear in messages (inline reminder messages WP7, plan 2.5c D11). The server runs the SAME
                                 rule teamio-utility uses to fill the provider variable over every visible provider (get-apt-reminder-settings
                                 res[5]); this only displays it. Same gate as the message boxes; hidden when the server sent no list. -->
                            <div v-if="showProviderNames" class="provider-names mt-4" data-test="provider-names">
                                <p class="fw-semi-bold mb-1">How your providers appear in messages</p>
                                <p class="text-muted small mb-2">A message that uses <code v-pre>{{apt_provider}}</code> names the provider on the appointment (the hygienist on a hygiene appointment), written like this.</p>
                                <ul class="provider-names__list">
                                    <li
                                        v-for="p in providerNameRows"
                                        :key="p.key"
                                        class="provider-names__row"
                                        :class="{ 'provider-names__row--left-out': p.leftOut }"
                                        data-test="provider-name-row">
                                        <span class="provider-names__abbr" data-test="provider-name-abbr">{{ p.abbr }}</span>
                                        <span class="provider-names__arrow" aria-hidden="true">&rarr;</span>
                                        <span class="provider-names__appears">{{ p.appears }}</span>
                                        <span class="provider-names__meta">
                                            <span v-if="p.reason" class="provider-names__reason">{{ p.reason }}</span>
                                            <span class="provider-names__count">{{ p.upcomingText }}</span>
                                        </span>
                                    </li>
                                </ul>
                                <p class="provider-names__hint text-muted small mb-0">{{ providerHint }}</p>
                            </div>
                            <!-- /provider-names -->
                        </div>
                        <div class="mt-5">
                                <h1 class="page-title text-center">Appointment Confirmations and Reminders</h1>
                        </div>
                        <div class="info-section">
                            <div style="float:left;">
                                <b-form-checkbox                                                    
                                    v-model="textConfirmations"
                                    value="TRUE"
                                    unchecked-value="FALSE"                 
                                    class=""
                                    switch
                                    size="lg"                                
                                />                            
                            </div>
                            <div>
                                <h3 class="fw-semi-bold sec_header">Text Confirmations</h3>
                                <p v-if="smsOutboundOff" class="grey-text" data-test="text-reminders-whatsapp-note">{{ textWhatsAppNote }}</p>
                            </div>
                            <!-- Once, at the top of the text sections: which message goes out on WhatsApp in place of the text templates below. -->
                            <WhatsAppReminderTemplateNote v-if="showWhatsAppTemplateNote" :siteID="siteID" />
                            <div class="time-for-confirmation">
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold">Time of day to send Confirmation:</p>
                                </div>
                                <div style="clear:right;">                                
                                    <select class="custom-select" v-model="textConfirmationTimeOfDayToSend">
                                        <option value="8">8 AM</option>
                                        <option value="9">9 AM</option>
                                        <option value="10">10 AM</option>
                                        <option value="11">11 AM</option>
                                        <option value="12">12 PM</option>
                                    </select>
                                </div>
                            </div>                        
                            <div class="conf-template-panel mt-5">
                                <template v-if="!slotIsBox('textConfirmTplForSingleApt')">
                                <p class="fw-semi-bold">Template for a single appointment:</p>
                                <multiselect                                    
                                    v-model="textConfirmTplForSingleApt"
                                    :options="aptReminderSmsTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="true"                                    
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />                                                   
                                <p v-if="canSwitchToPlain('textConfirmTplForSingleApt')" class="plain-switch" data-test="switch-to-plain-textConfirmTplForSingleApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['textConfirmTplForSingleApt']" @click="switchToPlain('textConfirmTplForSingleApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-textConfirmTplForSingleApt"
                                    v-bind="boxProps('textConfirmTplForSingleApt', 'Template for a single appointment:')"
                                    @input="setInlineText('textConfirmTplForSingleApt', $event)"
                                    @wiki="useWikiTemplate('textConfirmTplForSingleApt')"
                                    @spanish-input="setSpanishText('textConfirmTplForSingleApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('textConfirmTplForSingleApt')" />
                            </div>
                            
                            <div class="conf-template-panel mt-5">
                                <template v-if="!slotIsBox('textConfirmTplForMultiApt')">
                                <p class="fw-semi-bold">Template for multiple family member appointments on same day:</p>
                                <multiselect                                    
                                    v-model="textConfirmTplForMultiApt"
                                    :options="aptReminderSmsTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"                                    
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('textConfirmTplForMultiApt')" class="plain-switch" data-test="switch-to-plain-textConfirmTplForMultiApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['textConfirmTplForMultiApt']" @click="switchToPlain('textConfirmTplForMultiApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-textConfirmTplForMultiApt"
                                    v-bind="boxProps('textConfirmTplForMultiApt', 'Template for multiple family member appointments on same day:')"
                                    @input="setInlineText('textConfirmTplForMultiApt', $event)"
                                    @wiki="useWikiTemplate('textConfirmTplForMultiApt')"
                                    @spanish-input="setSpanishText('textConfirmTplForMultiApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('textConfirmTplForMultiApt')" />
                            </div>

                            <div class="conf-template-panel mt-5">
                                <template v-if="!slotIsBox('textConfirmTplForAutoReply')">
                                <p class="fw-semi-bold">Template for auto-reply to patients confirming their appointment:</p>
                                <multiselect                                    
                                    v-model="textConfirmTplForAutoReply"
                                    :options="aptReminderSmsTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"                                    
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('textConfirmTplForAutoReply')" class="plain-switch" data-test="switch-to-plain-textConfirmTplForAutoReply">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['textConfirmTplForAutoReply']" @click="switchToPlain('textConfirmTplForAutoReply')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-textConfirmTplForAutoReply"
                                    v-bind="boxProps('textConfirmTplForAutoReply', 'Template for auto-reply to patients confirming their appointment:')"
                                    @input="setInlineText('textConfirmTplForAutoReply', $event)"
                                    @wiki="useWikiTemplate('textConfirmTplForAutoReply')"
                                    @spanish-input="setSpanishText('textConfirmTplForAutoReply', $event)"
                                    @spanish-retranslate="retranslateSpanish('textConfirmTplForAutoReply')" />
                            </div>

                            <div style="clear:both;"></div>
                            <div class="confirmation-1 mt-5">                        
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="textConfirmOne"
                                        value="TRUE"
                                        unchecked-value="FALSE"/>                                
                                </div>
                                <div style="line-height:34px;float:left;"><p class="fw-semi-bold"><span class="h5 fw-bold">Confirmation # 1 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="textConfirmOneNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                                
                            </div>
                            <div style="clear:both;"></div>
                            <div class="confirmation-2">
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="textConfirmTwo"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />                                
                                </div>
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold"><span class="h5 fw-bold">Confirmation # 2 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="textConfirmTwoNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                            </div>
                            <div style="clear:both;"></div>
                            <div class="confirmation-3">
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="textConfirmThree"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />                                
                                </div>
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold"><span class="h5 fw-bold">Confirmation # 3 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="textConfirmThreeNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                            </div>
                            <div style="clear:both;"></div>
                            <div class="conf-keywords">
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold">Keywords to confirm appts:</p>
                                </div>
                                <div style="clear:right;">
                                    <b-form-input v-model="textConfirmKeywords"></b-form-input>
                                </div>
                            </div>
                        </div>
                        <div class="info-section">
                            <div style="float:left;">
                                <b-form-checkbox                                                      
                                    v-model="textReminders"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                    class=""
                                    switch
                                    size="lg"/>                
                            </div>
                            <div>
                                <h3 class="fw-semi-bold sec_header">Text Reminders</h3>
                            </div>
                            <!--
                            <b-form-group>
                                <b-form-checkbox
                                    v-model="doNotSendIfPatientConfirmReceivedOnSameDayAsNextReminder" 
                                    value="TRUE"                               
                                    unchecked-value="FALSE"
                                >
                                    <span class="fw-semi-bold">Don’t send reminder if patient confirmation received on same day as next scheduled reminder</span>
                                </b-form-checkbox>                                
                            </b-form-group>
                            -->
                            <b-form-group>
                                <b-form-checkbox
                                    v-model="skipTextReminderOnceConfirmed"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                >
                                    <span class="fw-semi-bold">Skip reminder texts once patient has confirmed. (Patient may still get same day reminder IF enabled below)</span>
                                </b-form-checkbox>
                                
                            </b-form-group>
                            <div class="time-for-reminder">
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold">Time of day to send Reminders:</p>
                                </div>
                                <div style="clear:right;">                                
                                    <select class="custom-select" v-model="textReminderTimeOfDayToSend">
                                        <option value="8">8 AM</option>
                                        <option value="9">9 AM</option>
                                        <option value="10">10 AM</option>
                                        <option value="11">11 AM</option>
                                        <option value="12">12 PM</option>
                                    </select>
                                </div>
                            </div>
                            
                            <div class="reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('textReminderTplForSingleApt')">
                                <p class="fw-semi-bold">Template for a single appointment:</p>
                                <multiselect                                    
                                    v-model="textReminderTplForSingleApt"
                                    :options="aptReminderSmsTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"                                    
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('textReminderTplForSingleApt')" class="plain-switch" data-test="switch-to-plain-textReminderTplForSingleApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['textReminderTplForSingleApt']" @click="switchToPlain('textReminderTplForSingleApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-textReminderTplForSingleApt"
                                    v-bind="boxProps('textReminderTplForSingleApt', 'Template for a single appointment:')"
                                    @input="setInlineText('textReminderTplForSingleApt', $event)"
                                    @wiki="useWikiTemplate('textReminderTplForSingleApt')"
                                    @spanish-input="setSpanishText('textReminderTplForSingleApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('textReminderTplForSingleApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div class="reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('textReminderTplForMultiApt')">
                                <p class="fw-semi-bold">Template for multiple family member appointments on same day:</p>
                                <multiselect                                    
                                    v-model="textReminderTplForMultiApt"
                                    :options="aptReminderSmsTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"                                    
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('textReminderTplForMultiApt')" class="plain-switch" data-test="switch-to-plain-textReminderTplForMultiApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['textReminderTplForMultiApt']" @click="switchToPlain('textReminderTplForMultiApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-textReminderTplForMultiApt"
                                    v-bind="boxProps('textReminderTplForMultiApt', 'Template for multiple family member appointments on same day:')"
                                    @input="setInlineText('textReminderTplForMultiApt', $event)"
                                    @wiki="useWikiTemplate('textReminderTplForMultiApt')"
                                    @spanish-input="setSpanishText('textReminderTplForMultiApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('textReminderTplForMultiApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div class="reminder-1 mt-5">
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="textReminderOne"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />                                
                                </div>
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold"><span class="h5 fw-bold">Reminder # 1 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="textReminderOneNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                            </div>
                            <div style="clear:both;"></div>
                            <div class="reminder-2">
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="textReminderTwo"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />                                
                                </div>
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold"><span class="h5 fw-bold">Reminder # 2 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="textReminderTwoNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                            </div>
                            <div style="clear:both;"></div>
                        </div>
                        <div class="info-section">
                            <div style="float:left;">
                                <b-form-checkbox                                                      
                                    v-model="textSameDayReminder"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                    class=""
                                    switch
                                    size="lg"/>                            
                            </div>
                            <div>
                                <h3 class="fw-semi-bold sec_header">Text Same Day Reminder</h3>
                            </div> 
                            <div style="clear:both;"></div>
                            <div class="same-day-reminder">
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold">Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="textSameDayReminderHoursBefore">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">hour(s) before the appointment.</p></div>
                            </div> 
                            <div class="time-for-reminder">
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold">Earliest time to send these reminders:</p>
                                </div>
                                <div style="clear:right;">
                                    
                                    <select class="custom-select" v-model="textSameDayReminderEarliestHourToSend">
                                        <option value="8">8 AM</option>
                                        <option value="9">9 AM</option>
                                        <option value="10">10 AM</option>
                                        <option value="11">11 AM</option>
                                        <option value="12">12 PM</option>
                                    </select>
                                </div>
                            </div> 
                            
                            <div class="reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('textSameDayReminderTplForSingleApt')">
                                <p class="fw-semi-bold">Template for a single appointment:</p>
                                <multiselect                                    
                                    v-model="textSameDayReminderTplForSingleApt"
                                    :options="aptReminderSmsTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"                                    
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('textSameDayReminderTplForSingleApt')" class="plain-switch" data-test="switch-to-plain-textSameDayReminderTplForSingleApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['textSameDayReminderTplForSingleApt']" @click="switchToPlain('textSameDayReminderTplForSingleApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-textSameDayReminderTplForSingleApt"
                                    v-bind="boxProps('textSameDayReminderTplForSingleApt', 'Template for a single appointment:')"
                                    @input="setInlineText('textSameDayReminderTplForSingleApt', $event)"
                                    @wiki="useWikiTemplate('textSameDayReminderTplForSingleApt')"
                                    @spanish-input="setSpanishText('textSameDayReminderTplForSingleApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('textSameDayReminderTplForSingleApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div class="reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('textSameDayReminderTplForMultiApt')">
                                <p class="fw-semi-bold">Template for multiple family member appointments on same day:</p>
                                <multiselect                                    
                                    v-model="textSameDayReminderTplForMultiApt"
                                    :options="aptReminderSmsTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"                                    
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('textSameDayReminderTplForMultiApt')" class="plain-switch" data-test="switch-to-plain-textSameDayReminderTplForMultiApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['textSameDayReminderTplForMultiApt']" @click="switchToPlain('textSameDayReminderTplForMultiApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-textSameDayReminderTplForMultiApt"
                                    v-bind="boxProps('textSameDayReminderTplForMultiApt', 'Template for multiple family member appointments on same day:')"
                                    @input="setInlineText('textSameDayReminderTplForMultiApt', $event)"
                                    @wiki="useWikiTemplate('textSameDayReminderTplForMultiApt')"
                                    @spanish-input="setSpanishText('textSameDayReminderTplForMultiApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('textSameDayReminderTplForMultiApt')" />
                            </div>
                        </div>
                        <div class="info-section">
                            <div style="float:left;">
                                <b-form-checkbox                                                    
                                    v-model="emailConfirmations"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                    class=""
                                    switch
                                    size="lg"                        
                                    />                            
                            </div>
                            <div>
                                <h3 class="fw-semi-bold sec_header">Email Confirmations</h3>
                            </div>  
                            
                            <div class="conf-template-panel mt-5">
                                <template v-if="!slotIsBox('emailConfirmationTplForSingleApt')">
                                <p class="fw-semi-bold">Template for a single appointment:</p>
                                <multiselect
                                    v-model="emailConfirmationTplForSingleApt"
                                    :options="aptReminderEmailTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('emailConfirmationTplForSingleApt')" class="plain-switch" data-test="switch-to-plain-emailConfirmationTplForSingleApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['emailConfirmationTplForSingleApt']" @click="switchToPlain('emailConfirmationTplForSingleApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-emailConfirmationTplForSingleApt"
                                    v-bind="boxProps('emailConfirmationTplForSingleApt', 'Template for a single appointment:')"
                                    @input="setInlineText('emailConfirmationTplForSingleApt', $event)"
                                    @update:subject="setInlineSubject('emailConfirmationTplForSingleApt', $event)"
                                    @wiki="useWikiTemplate('emailConfirmationTplForSingleApt')"
                                    @spanish-input="setSpanishText('emailConfirmationTplForSingleApt', $event)"
                                    @spanish-subject-input="setSpanishSubject('emailConfirmationTplForSingleApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('emailConfirmationTplForSingleApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div class="conf-template-panel mt-5">
                                <template v-if="!slotIsBox('emailConfirmationTplForMultiApt')">
                                <p class="fw-semi-bold">Template for multiple family member appointments on same day:</p>
                                <multiselect
                                    v-model="emailConfirmationTplForMultiApt"
                                    :options="aptReminderEmailTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('emailConfirmationTplForMultiApt')" class="plain-switch" data-test="switch-to-plain-emailConfirmationTplForMultiApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['emailConfirmationTplForMultiApt']" @click="switchToPlain('emailConfirmationTplForMultiApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-emailConfirmationTplForMultiApt"
                                    v-bind="boxProps('emailConfirmationTplForMultiApt', 'Template for multiple family member appointments on same day:')"
                                    @input="setInlineText('emailConfirmationTplForMultiApt', $event)"
                                    @update:subject="setInlineSubject('emailConfirmationTplForMultiApt', $event)"
                                    @wiki="useWikiTemplate('emailConfirmationTplForMultiApt')"
                                    @spanish-input="setSpanishText('emailConfirmationTplForMultiApt', $event)"
                                    @spanish-subject-input="setSpanishSubject('emailConfirmationTplForMultiApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('emailConfirmationTplForMultiApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div class="email-confirmation-1 mt-5">                        
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="emailConfirmOne"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />                                
                                </div>
                                <div style="line-height:34px;float:left;"><p class="fw-semi-bold"><span class="h5 fw-bold">Email Confirmation # 1 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="emailConfirmOneNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                                
                            </div>
                            <div style="clear:both;"></div>
                            <div class="email-confirmation-2">
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="emailConfirmTwo"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />                                
                                </div>
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold"><span class="h5 fw-bold">Email Confirmation # 2 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="emailConfirmTwoNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                            </div>
                            <div style="clear:both;"></div>
                            <!--
                              Cancellation alerts. Patients who click "I need to Cancel" used to be
                              recorded silently — no alert reached the office at all. Default ON.
                            -->
                            <b-form-group class="decline-alerts">
                                <b-form-checkbox
                                    v-model="declineNotificationsEnabled"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                >
                                    <span class="fw-semi-bold">Email the office when a patient clicks “I need to Cancel”</span>
                                </b-form-checkbox>
                                <div class="decline-emails" v-if="declineNotificationsEnabled !== 'FALSE'">
                                    <p class="fw-semi-bold">Send cancellation alerts to:</p>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="declineNotificationEmails"
                                        placeholder="frontdesk@yourpractice.com, manager@yourpractice.com"
                                    />
                                    <small class="text-muted">
                                        Separate multiple addresses with commas. If left blank, alerts go to your
                                        practice’s verified email address.
                                    </small>
                                </div>
                            </b-form-group>
                        </div>
                        <div class="info-section">
                            <div style="float:left;">
                                <b-form-checkbox
                                    v-model="emailReminders"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                    class=""
                                    switch
                                    size="lg"                        
                                    />                            
                            </div>
                            <div>
                                <h3 class="fw-semi-bold sec_header">Email Reminders</h3>
                            </div>
                            
                            <div class="email-reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('emailReminderTplForSingleApt')">
                                <p class="fw-semi-bold">Template for a single appointment:</p>
                                <multiselect
                                    v-model="emailReminderTplForSingleApt"
                                    :options="aptReminderEmailTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('emailReminderTplForSingleApt')" class="plain-switch" data-test="switch-to-plain-emailReminderTplForSingleApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['emailReminderTplForSingleApt']" @click="switchToPlain('emailReminderTplForSingleApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-emailReminderTplForSingleApt"
                                    v-bind="boxProps('emailReminderTplForSingleApt', 'Template for a single appointment:')"
                                    @input="setInlineText('emailReminderTplForSingleApt', $event)"
                                    @update:subject="setInlineSubject('emailReminderTplForSingleApt', $event)"
                                    @wiki="useWikiTemplate('emailReminderTplForSingleApt')"
                                    @spanish-input="setSpanishText('emailReminderTplForSingleApt', $event)"
                                    @spanish-subject-input="setSpanishSubject('emailReminderTplForSingleApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('emailReminderTplForSingleApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div class="email-reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('emailReminderTplForMultiApt')">
                                <p class="fw-semi-bold">Template for multiple family member appointments on same day:</p>
                                <multiselect
                                    v-model="emailReminderTplForMultiApt"
                                    :options="aptReminderEmailTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('emailReminderTplForMultiApt')" class="plain-switch" data-test="switch-to-plain-emailReminderTplForMultiApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['emailReminderTplForMultiApt']" @click="switchToPlain('emailReminderTplForMultiApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-emailReminderTplForMultiApt"
                                    v-bind="boxProps('emailReminderTplForMultiApt', 'Template for multiple family member appointments on same day:')"
                                    @input="setInlineText('emailReminderTplForMultiApt', $event)"
                                    @update:subject="setInlineSubject('emailReminderTplForMultiApt', $event)"
                                    @wiki="useWikiTemplate('emailReminderTplForMultiApt')"
                                    @spanish-input="setSpanishText('emailReminderTplForMultiApt', $event)"
                                    @spanish-subject-input="setSpanishSubject('emailReminderTplForMultiApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('emailReminderTplForMultiApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div class="email-reminder-1 mt-5">
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="emailReminderOne"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />                                
                                </div>
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold"><span class="h5 fw-bold">Email Reminder # 1 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="emailReminderOneNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                            </div>
                            <div style="clear:both;"></div>
                            <div class="email-reminder-2">
                                <div style="float:left;">
                                    <b-form-checkbox
                                        v-model="emailReminderTwo"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                    />                                
                                </div>
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold"><span class="h5 fw-bold">Email Reminder # 2 : </span> Send</p>
                                </div>
                                <div style="float:left;">
                                    <select class="custom-select" v-model="emailReminderTwoNumOfDays">
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>    
                                        <option value="7">7</option>
                                        <option value="8">8</option>
                                        <option value="9">9</option>
                                        <option value="10">10</option>
                                        <option value="11">11</option>
                                        <option value="12">12</option>
                                        <option value="13">13</option>
                                        <option value="14">14</option>
                                        <option value="15">15</option>
                                        <option value="16">16</option>
                                        <option value="17">17</option>
                                        <option value="18">18</option>
                                        <option value="19">19</option>
                                        <option value="20">20</option>
                                        <option value="21">21</option>
                                    </select>
                                </div>
                                <div style="line-height:34px;clear:right;"><p class="fw-semi-bold">days before the appointment.</p></div>
                            </div>    
                        </div>
                        <div class="info-section">
                            <div style="float:left;">
                                <b-form-checkbox                                                      
                                    v-model="premedReminders"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                    class=""
                                    switch
                                    size="lg"                        
                                    />                            
                            </div>                        
                            <div>
                                <h3 class="fw-semi-bold sec_header">Pre-Med Reminders</h3>
                                <p class="fw-semi-bold">Pre-med reminder text message will be sent a day before the appointment.</p>
                            </div>
                            <div class="time-for-reminder">
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold">Time of day to send Premed Reminders:</p>
                                </div>
                                <div style="clear:right;">                                
                                    <select class="custom-select" v-model="premedReminderTimeOfDay">
                                        <option value="7">7 AM</option>
                                        <option value="8">8 AM</option>
                                        <option value="9">9 AM</option>
                                        <option value="10">10 AM</option>
                                        <option value="11">11 AM</option>
                                        <option value="12">12 PM</option>
                                    </select>
                                </div>
                            </div>
                            <div class="conf-template-panel mt-5">
                                <template v-if="!slotIsBox('premedTpl')">
                                <p class="fw-semi-bold">Template for Pre-Med Reminder:</p>
                                <multiselect
                                    v-model="premedTpl"
                                    :options="aptReminderSmsTemplates"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('premedTpl')" class="plain-switch" data-test="switch-to-plain-premedTpl">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['premedTpl']" @click="switchToPlain('premedTpl')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-premedTpl"
                                    v-bind="boxProps('premedTpl', 'Template for Pre-Med Reminder:')"
                                    @input="setInlineText('premedTpl', $event)"
                                    @wiki="useWikiTemplate('premedTpl')"
                                    @spanish-input="setSpanishText('premedTpl', $event)"
                                    @spanish-retranslate="retranslateSpanish('premedTpl')" />
                            </div>

                        </div>
                        <div class="info-section">
                            <div style="float:left;">
                                <b-form-checkbox
                                    v-model="apptBookedConfirmation"
                                    value="TRUE"
                                    unchecked-value="FALSE"
                                    class=""
                                    switch
                                    size="lg"
                                    />
                            </div>
                            <div>
                                <h3 class="fw-semi-bold sec_header">Appointment Booked Confirmation</h3>
                                <p class="fw-semi-bold">Automatically send a confirmation the moment a team member schedules an appointment in Open Dental — so patients know their booking went through. Choose SMS, email, or both, and pick the Wiki template for each.</p>
                                <p class="grey-text">Appointments patients book themselves through online scheduling are skipped here, since they already receive a confirmation.</p>
                                <p class="grey-text">When an existing appointment is moved to a different day, the confirmation goes out a few minutes later, so a move that is cancelled right away does not text the patient. Moves within the same day, and appointments in an ignored operatory (above), are not sent.</p>
                            </div>

                            <div v-if="apptBookedConfirmation === 'TRUE'" class="apt-booked-options mt-4">
                                <p class="fw-semi-bold">How should we notify the patient?</p>
                                <label class="form-check form-check-inline d-block">
                                    <input type="checkbox" class="form-check-input" v-model="apptBookedSms" :disabled="smsOutboundOff && !apptBookedSms" />
                                    <span class="form-check-label-text">Send via SMS</span>
                                </label>
                                <p v-if="smsOutboundOff" class="grey-text" data-test="booked-sms-off-note">{{ smsDisabledMessage }} This confirmation can only go out by email.</p>
                                <label class="form-check form-check-inline d-block mt-2">
                                    <input type="checkbox" class="form-check-input" v-model="apptBookedEmail" />
                                    <span class="form-check-label-text">Send via Email</span>
                                </label>

                                <div v-if="apptBookedSms" class="conf-template-panel mt-4">
                                    <template v-if="!slotIsBox('apptBookedSmsTpl')">
                                    <p class="fw-semi-bold">SMS Template for Appointment Booked Confirmation:</p>
                                    <multiselect
                                        v-model="apptBookedSmsTpl"
                                        :options="apptBookedSmsTemplates"
                                        :multiple="false"
                                        :taggable="true"
                                        :show-labels="false"
                                        label="Name"
                                        track-by="WikiPageID"
                                        aria-placeholder="Select a Template"
                                    />
                                    <p class="grey-text mt-2">Only shows Wiki pages tagged <strong>apt-booked</strong> and <strong>sms-template</strong>. To make a template appear here, add both tags to it in the Wiki.</p>
                                    <p v-if="canSwitchToPlain('apptBookedSmsTpl')" class="plain-switch" data-test="switch-to-plain-apptBookedSmsTpl">
                                        Wiki page template &middot;
                                        <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['apptBookedSmsTpl']" @click="switchToPlain('apptBookedSmsTpl')">Switch to a plain message</button>
                                    </p>
                                    </template>
                                    <MessageTemplateBox
                                        v-else
                                        ref="box-apptBookedSmsTpl"
                                        v-bind="boxProps('apptBookedSmsTpl', 'SMS Template for Appointment Booked Confirmation:')"
                                        @input="setInlineText('apptBookedSmsTpl', $event)"
                                        @wiki="useWikiTemplate('apptBookedSmsTpl')"
                                        @spanish-input="setSpanishText('apptBookedSmsTpl', $event)"
                                        @spanish-retranslate="retranslateSpanish('apptBookedSmsTpl')" />
                                </div>

                                <div v-if="apptBookedEmail" class="conf-template-panel mt-4">
                                    <template v-if="!slotIsBox('apptBookedEmailTpl')">
                                    <p class="fw-semi-bold">Email Template for Appointment Booked Confirmation:</p>
                                    <multiselect
                                        v-model="apptBookedEmailTpl"
                                        :options="apptBookedEmailTemplates"
                                        :multiple="false"
                                        :taggable="true"
                                        :show-labels="false"
                                        label="Name"
                                        track-by="WikiPageID"
                                        aria-placeholder="Select a Template"
                                    />
                                    <p class="grey-text mt-2">Only shows Wiki pages tagged <strong>apt-booked</strong> and <strong>email-template</strong>. To make a template appear here, add both tags to it in the Wiki.</p>
                                    <p v-if="canSwitchToPlain('apptBookedEmailTpl')" class="plain-switch" data-test="switch-to-plain-apptBookedEmailTpl">
                                        Wiki page template &middot;
                                        <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['apptBookedEmailTpl']" @click="switchToPlain('apptBookedEmailTpl')">Switch to a plain message</button>
                                    </p>
                                    </template>
                                    <MessageTemplateBox
                                        v-else
                                        ref="box-apptBookedEmailTpl"
                                        v-bind="boxProps('apptBookedEmailTpl', 'Email Template for Appointment Booked Confirmation:')"
                                        @input="setInlineText('apptBookedEmailTpl', $event)"
                                        @update:subject="setInlineSubject('apptBookedEmailTpl', $event)"
                                        @wiki="useWikiTemplate('apptBookedEmailTpl')"
                                        @spanish-input="setSpanishText('apptBookedEmailTpl', $event)"
                                        @spanish-subject-input="setSpanishSubject('apptBookedEmailTpl', $event)"
                                        @spanish-retranslate="retranslateSpanish('apptBookedEmailTpl')" />
                                </div>
                            </div>

                        </div>
                        <div class="mt-5">
                                <h1 class="page-title text-center">Automated Recall Reminders</h1>
                        </div>
                        <div class="info-section">
                            <div style="float:left;">
                                <b-form-checkbox       
                                    v-model="recallRemindersOn"
                                    value="TRUE"
                                    unchecked-value="FALSE"                                    
                                    class=""
                                    switch
                                    size="lg"                     
                                    />                            
                            </div>                        
                            <div>
                                <h3 class="fw-bold h2 ">Send Auto Recall Reminders</h3>
                                <p v-if="recallWhatsAppWarning" class="grey-text" data-test="recall-whatsapp-warning">{{ recallWhatsAppWarning }}</p>
                                <p class="fw-semi-bold">Teamio automatically checks your recall list to see who's due for a recall visit and then sends them a text and/or email depending on your settings below.</p>
                                <p class="fw-semi-bold">If this setting is turned off, no auto recall reminders will be sent.</p>
                                <div v-if="recallSentByCloud" class="recall-leg-badge" data-test="recall-send-leg-backend">
                                    <span class="badge badge-success">Teamio Cloud</span>
                                    <span class="ml-2">Your <strong>first</strong> recall reminder is sent by Teamio Cloud at the time of day you choose below. Turning this switch off stops <strong>all</strong> automated recall reminders, including the ones Teamio Cloud sends.</span>
                                </div>
                            </div>

                        </div>

                        <div class="info-section">

                            <div>                            
                                <p class="fw-semi-bold">Recall Reminder Providers:</p>
                                <multiselect
                                    v-if="providers"
                                    v-model="recallReminderProviders"
                                    :options="providers"
                                    :multiple="true"
                                    :taggable="false"
                                    :show-labels="false" 
                                    :custom-label="providerCustomLabel"                                   
                                    label="FName"
                                    track-by="ProvNum"
                                    aria-placeholder="Select Providers"
                                >
                                    <template slot="singleLabel" slot-scope="props">
                                        <span class="custom__tag">
                                        <img
                                            class="option__image"
                                            src="../../../assets/icons-user-40.png"
                                            alt="Team member image"
                                        />
                                        <span>{{ providerCustomLabel(props.option) }}</span>
                                        <i tabindex="1" class="multiselect__tag-icon" @click="removeProvider(props.option)"></i>
                                        </span>
                                    </template>
                                    <template slot="option" slot-scope="props">
                                        <img
                                        class="option__image"
                                        src="../../../assets/icons-user-40.png"
                                        alt="Team member image"
                                        />
                                        <div class="option__desc">
                                        <span class="option__title">{{ providerCustomLabel(props.option) }}</span>
                                        </div>
                                    </template>
                                </multiselect>
                            </div>
                            
                            <div class="time-for-reminder mt-5">
                                <div style="float:left;line-height:34px;"><p class="fw-semi-bold">Time of day to send Auto Text Reminder for Recall:</p>
                                </div>
                                <div style="clear:right;">
                                    <select class="custom-select" v-model="recallReminderTimeOfDayToSend">
                                        <option value="8">8 AM</option>
                                        <option value="9">9 AM</option>
                                        <option value="10">10 AM</option>
                                        <option value="11">11 AM</option>
                                        <option value="12">12 PM</option>
                                    </select>
                                </div>
                            </div>

                            <div class="recall-send-timing-section mt-5">
                                <p class="fw-semi-bold">When to Send Recall Reminders</p>
                                <p class="grey-text">Choose whether to send recall reminders on the day each patient is due, or send them in advance with a custom lead time.</p>
                                <div class="recall-send-timing-radios">
                                    <label class="form-check form-check-inline d-block">
                                        <input type="radio" class="form-check-input" value="ON_DUE_DATE" v-model="recallReminderSendTiming" />
                                        <span class="form-check-label-text">Send on the day the patient is due for recall</span>
                                    </label>
                                    <label class="form-check form-check-inline d-block mt-2">
                                        <input type="radio" class="form-check-input" value="DAYS_BEFORE" v-model="recallReminderSendTiming" />
                                        <span class="form-check-label-text">
                                            Send a custom number of days before they're due:
                                            <b-form-input
                                                type="number"
                                                min="1"
                                                v-model.number="recallReminderDaysBefore"
                                                :disabled="recallReminderSendTiming !== 'DAYS_BEFORE'"
                                                placeholder="e.g. 31"
                                                style="width: 90px; display: inline-block; margin-left: 6px; margin-right: 6px;"
                                            />
                                            days before
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div class="recall-mode-section mt-5">
                                <p class="fw-semi-bold">For Recall Patients</p>
                                <p class="grey-text">Choose whether to send the same Recall reminder templates to all recall patients, or to send different templates based on the patient's Recall Type.</p>
                                <div class="recall-mode-radios">
                                    <label class="form-check form-check-inline mr-4">
                                        <input type="radio" class="form-check-input" value="ALL" v-model="recallReminderMode" />
                                        <span class="form-check-label-text">For All Recall Patients <small class="text-muted">(regardless of recall type)</small></span>
                                    </label>
                                    <label class="form-check form-check-inline">
                                        <input type="radio" class="form-check-input" value="SPECIFIC_TYPE" v-model="recallReminderMode" />
                                        <span class="form-check-label-text">Specific Recall Type</span>
                                    </label>
                                </div>
                            </div>

                            <div v-if="recallReminderMode === 'SPECIFIC_TYPE'" class="recall-type-rules mt-4">
                                <p v-if="!useInlineMessages" class="grey-text">Define which SMS and Email templates to send for each Recall Type. Leave a template blank to disable that channel for the row.</p>
                                <p v-else class="grey-text" data-test="rule-cards-intro">Choose the text message and the email to send for each Recall Type. Turn a channel off to send nothing on it for those recall types.</p>

                                <!-- Plain message boxes (the new view): one card per rule — its recall types, then a text message and an
                                     email, each a plain message, a wiki template or off. The classic table below is the dropdown view. -->
                                <div v-if="useInlineMessages" class="rule-cards" data-test="recall-rule-cards">
                                    <div
                                        v-for="(rule, index) in recallReminderTypeRules"
                                        :key="'recall-type-rule-card-' + (rule && rule.id ? rule.id : 'row-' + index)"
                                        class="rule-card"
                                        :class="{ 'is-off': !ruleSectionOn() }"
                                        data-test="recall-rule-card"
                                        :data-rule-index="index"
                                    >
                                        <div class="rule-card__head">
                                            <span class="rule-card__title">Recall type rule {{ index + 1 }}</span>
                                            <button type="button" class="btn btn-link p-0" title="Remove rule" aria-label="Remove this rule" data-test="rule-remove" @click="removeRecallTypeRule(index)">
                                                <i class="la la-times"></i>
                                            </button>
                                        </div>
                                        <p class="fw-semi-bold rule-card__label">Recall types</p>
                                        <multiselect
                                            v-model="rule.recallTypes"
                                            :options="recallTypeOptions"
                                            :multiple="true"
                                            :show-labels="false"
                                            :disabled="!ruleSectionOn()"
                                            label="Description"
                                            track-by="RecallTypeNum"
                                            placeholder="Select Recall Type(s)"
                                        />
                                        <div v-for="channel in ['sms', 'email']" :key="channel" class="rule-channel" :data-channel="channel" data-test="rule-channel">
                                            <!-- A plain message -->
                                            <template v-if="ruleState(rule, channel) === 'inline'">
                                                <MessageTemplateBox
                                                    :ref="'box-' + ruleKeyOf(rule, channel)"
                                                    v-bind="ruleBoxProps(index, channel)"
                                                    @input="setInlineText(ruleKeyOf(rule, channel), $event)"
                                                    @update:subject="setInlineSubject(ruleKeyOf(rule, channel), $event)"
                                                    @wiki="useWikiTemplate(ruleKeyOf(rule, channel))"
                                                    @booking-link="setBookingLink(ruleKeyOf(rule, channel), $event)"
                                                    @spanish-input="setSpanishText(ruleKeyOf(rule, channel), $event)"
                                                    @spanish-subject-input="setSpanishSubject(ruleKeyOf(rule, channel), $event)"
                                                    @spanish-retranslate="retranslateSpanish(ruleKeyOf(rule, channel))" />
                                                <p v-if="ruleSectionOn()" class="rule-links">
                                                    <button type="button" class="rule-links__btn" data-test="rule-turn-off" @click="turnRuleChannelOff(index, channel)">{{ channel === 'sms' ? "Don't send a text for these recall types" : "Don't send an email for these recall types" }}</button>
                                                </p>
                                            </template>
                                            <!-- A wiki template: today's dropdown -->
                                            <template v-else-if="ruleState(rule, channel) === 'wiki'">
                                                <p class="fw-semi-bold">{{ channel === 'sms' ? 'Text message for these recall types:' : 'Email for these recall types:' }}</p>
                                                <multiselect
                                                    v-model="rule[channel === 'sms' ? 'smsTemplate' : 'emailTemplate']"
                                                    :options="channel === 'sms' ? recallReminderSmsTempaltes : recallReminderEmailTempaltes"
                                                    :multiple="false"
                                                    :show-labels="false"
                                                    :allow-empty="true"
                                                    label="Name"
                                                    track-by="WikiPageID"
                                                    :placeholder="channel === 'sms' ? 'Select SMS Template' : 'Select Email Template'"
                                                />
                                                <p class="rule-links" data-test="rule-wiki-links">
                                                    Wiki page template
                                                    <template v-if="canSwitchRuleToPlain(index, channel)">
                                                        &middot; <button type="button" class="rule-links__btn" :disabled="!!slotBusy[ruleKeyOf(rule, channel)]" data-test="rule-switch-to-plain" @click="switchRuleToPlain(index, channel)">Switch to a plain message</button>
                                                    </template>
                                                    <template v-if="ruleSectionOn()">
                                                        &middot; <button type="button" class="rule-links__btn" data-test="rule-turn-off" @click="turnRuleChannelOff(index, channel)">{{ channel === 'sms' ? "Don't send a text for these recall types" : "Don't send an email for these recall types" }}</button>
                                                    </template>
                                                </p>
                                            </template>
                                            <!-- Off -->
                                            <p v-else class="rule-off" data-test="rule-channel-off">
                                                {{ channel === 'sms' ? 'No text message for these recall types' : 'No email for these recall types' }}
                                                <template v-if="ruleSectionOn() && (channel === 'email' || !smsOutboundOff)">
                                                    &middot; <button type="button" class="rule-links__btn" data-test="rule-add-channel" @click="addRuleChannel(index, channel)">{{ channel === 'sms' ? 'Add a text message' : 'Add an email' }}</button>
                                                </template>
                                            </p>
                                        </div>
                                    </div>
                                    <div class="text-center mt-3">
                                        <button type="button" class="btn btn-link" data-test="rule-add" @click="addRecallTypeRule">
                                            <i class="la la-plus"></i> Add Rule
                                        </button>
                                    </div>
                                </div>

                                <div v-else class="rule-table">
                                    <div class="rule-header d-flex">
                                        <div class="col-recall-type flex-fill px-2"><strong>RECALL TYPE</strong></div>
                                        <div class="col-sms-template flex-fill px-2"><strong>SMS TEMPLATE</strong></div>
                                        <div class="col-email-template flex-fill px-2"><strong>EMAIL TEMPLATE</strong></div>
                                        <div class="col-action px-2" style="width: 40px;"></div>
                                    </div>
                                    <div
                                        v-for="(rule, index) in recallReminderTypeRules"
                                        :key="'recall-type-rule-' + index"
                                        class="rule-row d-flex align-items-start mt-2"
                                    >
                                        <div class="col-recall-type flex-fill px-2">
                                            <multiselect
                                                v-model="rule.recallTypes"
                                                :options="recallTypeOptions"
                                                :multiple="true"
                                                :show-labels="false"
                                                label="Description"
                                                track-by="RecallTypeNum"
                                                placeholder="Select Recall Type(s)"
                                            />
                                        </div>
                                        <div class="col-sms-template flex-fill px-2">
                                            <multiselect
                                                v-model="rule.smsTemplate"
                                                :options="recallReminderSmsTempaltes"
                                                :multiple="false"
                                                :show-labels="false"
                                                :allow-empty="true"
                                                label="Name"
                                                track-by="WikiPageID"
                                                placeholder="Select SMS Template"
                                            />
                                        </div>
                                        <div class="col-email-template flex-fill px-2">
                                            <multiselect
                                                v-model="rule.emailTemplate"
                                                :options="recallReminderEmailTempaltes"
                                                :multiple="false"
                                                :show-labels="false"
                                                :allow-empty="true"
                                                label="Name"
                                                track-by="WikiPageID"
                                                placeholder="Select Email Template"
                                            />
                                        </div>
                                        <div class="col-action px-2" style="width: 40px;">
                                            <button type="button" class="btn btn-link p-0" @click="removeRecallTypeRule(index)" title="Remove rule">
                                                <i class="la la-times"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div class="text-center mt-3">
                                        <button type="button" class="btn btn-link" @click="addRecallTypeRule">
                                            <i class="la la-plus"></i> Add Rule
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="recallReminderMode !== 'SPECIFIC_TYPE'" class="reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('recallReminderTplForSingleApt')">
                                <p class="fw-semi-bold">Automated Recall Text Reminder template for a single appointment:</p>
                                <multiselect
                                    v-model="recallReminderTplForSingleApt"
                                    :options="recallReminderSmsTempaltes"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('recallReminderTplForSingleApt')" class="plain-switch" data-test="switch-to-plain-recallReminderTplForSingleApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['recallReminderTplForSingleApt']" @click="switchToPlain('recallReminderTplForSingleApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-recallReminderTplForSingleApt"
                                    v-bind="boxProps('recallReminderTplForSingleApt', 'Automated Recall Text Reminder template for a single appointment:')"
                                    @input="setInlineText('recallReminderTplForSingleApt', $event)"
                                    @wiki="useWikiTemplate('recallReminderTplForSingleApt')"
                                    @booking-link="setBookingLink('recallReminderTplForSingleApt', $event)"
                                    @spanish-input="setSpanishText('recallReminderTplForSingleApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('recallReminderTplForSingleApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div v-if="recallReminderMode !== 'SPECIFIC_TYPE'" class="reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('recallReminderTplForMultiApt')">
                                <p class="fw-semi-bold">Automated Recall Text Reminder Template for multiple family members due for recall on the same day:</p>
                                <multiselect
                                    v-model="recallReminderTplForMultiApt"
                                    :options="recallReminderSmsTempaltes"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('recallReminderTplForMultiApt')" class="plain-switch" data-test="switch-to-plain-recallReminderTplForMultiApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['recallReminderTplForMultiApt']" @click="switchToPlain('recallReminderTplForMultiApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-recallReminderTplForMultiApt"
                                    v-bind="boxProps('recallReminderTplForMultiApt', 'Automated Recall Text Reminder Template for multiple family members due for recall on the same day:')"
                                    @input="setInlineText('recallReminderTplForMultiApt', $event)"
                                    @wiki="useWikiTemplate('recallReminderTplForMultiApt')"
                                    @booking-link="setBookingLink('recallReminderTplForMultiApt', $event)"
                                    @spanish-input="setSpanishText('recallReminderTplForMultiApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('recallReminderTplForMultiApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div v-if="recallReminderMode !== 'SPECIFIC_TYPE'" class="reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('recallReminderEmailTplForSingleApt')">
                                <p class="fw-semi-bold">Automated Recall Email Reminder Template for a single appointment:</p>
                                <multiselect
                                    v-model="recallReminderEmailTplForSingleApt"
                                    :options="recallReminderEmailTempaltes"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('recallReminderEmailTplForSingleApt')" class="plain-switch" data-test="switch-to-plain-recallReminderEmailTplForSingleApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['recallReminderEmailTplForSingleApt']" @click="switchToPlain('recallReminderEmailTplForSingleApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-recallReminderEmailTplForSingleApt"
                                    v-bind="boxProps('recallReminderEmailTplForSingleApt', 'Automated Recall Email Reminder Template for a single appointment:')"
                                    @input="setInlineText('recallReminderEmailTplForSingleApt', $event)"
                                    @update:subject="setInlineSubject('recallReminderEmailTplForSingleApt', $event)"
                                    @wiki="useWikiTemplate('recallReminderEmailTplForSingleApt')"
                                    @booking-link="setBookingLink('recallReminderEmailTplForSingleApt', $event)"
                                    @spanish-input="setSpanishText('recallReminderEmailTplForSingleApt', $event)"
                                    @spanish-subject-input="setSpanishSubject('recallReminderEmailTplForSingleApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('recallReminderEmailTplForSingleApt')" />
                            </div>
                            <div style="clear:both;"></div>
                            <div v-if="recallReminderMode !== 'SPECIFIC_TYPE'" class="reminder-template-panel mt-5">
                                <template v-if="!slotIsBox('recallReminderEmailTplForMultiApt')">
                                <p class="fw-semi-bold">Automated Recall Email Reminder for multiple family members due for recall on the same day:</p>
                                <multiselect
                                    v-model="recallReminderEmailTplForMultiApt"
                                    :options="recallReminderEmailTempaltes"
                                    :multiple="false"
                                    :taggable="true"
                                    :show-labels="false"
                                    label="Name"
                                    track-by="WikiPageID"
                                    aria-placeholder="Select a Template"
                                />
                                <p v-if="canSwitchToPlain('recallReminderEmailTplForMultiApt')" class="plain-switch" data-test="switch-to-plain-recallReminderEmailTplForMultiApt">
                                    Wiki page template &middot;
                                    <button type="button" class="plain-switch__btn" :disabled="!!slotBusy['recallReminderEmailTplForMultiApt']" @click="switchToPlain('recallReminderEmailTplForMultiApt')">Switch to a plain message</button>
                                </p>
                                </template>
                                <MessageTemplateBox
                                    v-else
                                    ref="box-recallReminderEmailTplForMultiApt"
                                    v-bind="boxProps('recallReminderEmailTplForMultiApt', 'Automated Recall Email Reminder for multiple family members due for recall on the same day:')"
                                    @input="setInlineText('recallReminderEmailTplForMultiApt', $event)"
                                    @update:subject="setInlineSubject('recallReminderEmailTplForMultiApt', $event)"
                                    @wiki="useWikiTemplate('recallReminderEmailTplForMultiApt')"
                                    @booking-link="setBookingLink('recallReminderEmailTplForMultiApt', $event)"
                                    @spanish-input="setSpanishText('recallReminderEmailTplForMultiApt', $event)"
                                    @spanish-subject-input="setSpanishSubject('recallReminderEmailTplForMultiApt', $event)"
                                    @spanish-retranslate="retranslateSpanish('recallReminderEmailTplForMultiApt')" />
                            </div>
                            <div style="clear:both;"></div>

                            <div class="info-section">
                                
                                <div class="maximum-panel">
                                <p class="fw-semi-bold">Maximum #of Reminders :
                                    <b-form-input type="number" v-model="recallReminderMaxNum" @input="onRecallReminderMaxNumChange"></b-form-input>
                                </p>                                
                                <p class="grey-text">This is the maximum numer of times you want to send a reminder to a patient.</p>                                
                                </div>

                                <div style="float:left;">
                                    <b-form-checkbox                                                      
                                        v-model="preferContactMethodAllowed"
                                        value="TRUE"
                                        unchecked-value="FALSE"
                                        class=""
                                        switch
                                        size="lg"                        
                                        />                            
                                </div>
                                <div>
                                    <h4 class="">Allow to Contact Patient using Prefer Contact Method</h4>
                                    <p class="grey-text">In case where a Patient's preferred contact method does not match the type chosen below</p>
                                </div>

                                <h3 class="mb-2 mt-5 fw-bold">Set Frequency of Reminders:</h3>
                                
                                <ul class="follow-list">
                                    <li v-for="(item,index) in recallReminderFollowUps" :key="index">
                                        Reminder #{{index+1}}: Send
                                        <select class="custom-select" v-model="item.recallType">
                                            <option value="text">Text</option>
                                            <option value="email">Email</option>
                                        </select>
                                        <select v-if="index !== 0" class="custom-select" v-model="item.duration">
                                            <option value="1">1</option>
                                            <option value="2">2</option>
                                            <option value="3">3</option>
                                            <option value="4">4</option>
                                            <option value="5">5</option>
                                            <option value="6">6</option>
                                            <option value="7">7</option>
                                            <option value="8">8</option>
                                            <option value="9">9</option>
                                            <option value="10">10</option>
                                        </select>
                                        <select v-if="index !== 0" class="custom-select" v-model="item.durationType">
                                            <option value="days">Day(s)</option>
                                            <option value="weeks">Week(s)</option>
                                            <option value="months">Month(s)</option>
                                        </select>
                                        <span v-if="index !== 0">after reminder # {{index}}.</span>
                                        <b-button v-if="index !== 0" variant="outline-none" size="sm" class="ml-2 delete-button" @click="removeFollowup(index)">
                                            <i class="fa fa-trash-o" aria-hidden="true"></i>
                                        </b-button>
                                    </li>
                                </ul>                                
                            </div>
                        </div>

                    <b-button variant="success" type="submit" class="btn-sm mt-2 ml-2" @click="save">Save</b-button>
                    <b-button variant="success" type="submit" class="btn-sm mt-2 ml-2" @click="setErrored2(false);setMessage2('');$emit('closePanel', {});">Cancel</b-button>
                    <p/>
                    <PopupErrorDisplay/>
                </b-col>
            </b-row>
        </b-container>        

        <b-modal ref="pause" id="pause" title="Pause Appointment Reminder Notifications" body-class="bg-white">
            <p>This will stop from any appointment reminders from going out. Are you sure?</p>
            <div slot="modal-footer" class="w-100">
                <b-button variant="danger" size="sm" class="float-right" @click="hideModal('pause')">No</b-button>
                <b-button variant="success" size="sm" class="float-right mr-3" @click="pause">Yes</b-button>
            </div>
        </b-modal>

        <!--
        <section>
            <Widget>
                <div class="button-panel">
                    <b-row>
                        <b-col md='4'>
                            <span class="fw-semi-bold">test1</span>
                        </b-col>
                        <b-col md='8'>
                            <span class="fw-semi-bold">test2</span>                    
                        </b-col>
                    </b-row>
                </div>
            </Widget>
        </section>
        -->
    </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import Widget from '@/components/Widget/Widget';
import mixin from '../../../mixin.js';
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import _ from 'lodash';
import { isSmsEnabled, hasSmsNumber, isEmailEnabled, isSmsOutboundDisabled, isOnlineSchedulingPaid, readSettingValue, SMS_DISABLED_MESSAGE } from '@/utils/siteCapability';
import { parseSignatureSetting, isSignatureConfigured } from '@/utils/emailSignature';
import { reminderTextSaveRefusal, whatsAppReminderCoverage, recallTextWarning } from '@/utils/whatsappReminderCoverage';
import WhatsAppReminderTemplateNote from './WhatsAppReminderTemplateNote.vue';
import Multiselect from "vue-multiselect";
import DatePicker from "vue2-datepicker";
import MessageTemplateBox from '@/components/MessageTemplateBox.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';
import {
    SLOT_DEFAULTS, getSlotDefault, isInlineSlot, buildInlineSlot, validateInlineSlots,
    RULE_CHANNEL_SLOT, inlineRuleId, isInlineRuleTemplate, buildInlineRuleTemplate, getRuleDefault,
    slotAllowsBookingLink, hasBookingLinkToken, pickDefaultText, bookableRecallAppointmentTypes,
} from '@/utils/reminderMessageDefaults';
import { findUnknownVariables } from '@/utils/templateVariables';
import { providerRowsForDisplay, PROVIDER_HINT } from '@/utils/reminderProviderNames';
import { wikiContentToPlainText, wikiContentToEmailText } from '@/utils/wikiPlainText';

// The 18 slots that can be a plain message box (inline reminder messages, plan
// docs/plans/2026-10-17-inline-reminder-messages-plan.md): the 11 SMS slots and the 7 EMAIL slots (a subject + a
// body). recallReminderTypeRules stays a wiki dropdown table until Phase 3. Derived from the defaults so a slot
// added there cannot be forgotten here (cypress/node-tests/apt-reminder-inline-messages.test.js pins the template
// to this list).
const INLINE_KEYS = SLOT_DEFAULTS.map(e => e.key);

// The switch that turns each slot's section on (a box is read-only while its section is off). null = no switch of
// its own: the booked-confirmation boxes only exist while their "Send via SMS" / "Send via Email" box is ticked.
const SLOT_SECTION = {
    textConfirmTplForSingleApt: 'textConfirmations',
    textConfirmTplForMultiApt: 'textConfirmations',
    textConfirmTplForAutoReply: 'textConfirmations',
    textReminderTplForSingleApt: 'textReminders',
    textReminderTplForMultiApt: 'textReminders',
    textSameDayReminderTplForSingleApt: 'textSameDayReminder',
    textSameDayReminderTplForMultiApt: 'textSameDayReminder',
    emailConfirmationTplForSingleApt: 'emailConfirmations',
    emailConfirmationTplForMultiApt: 'emailConfirmations',
    emailReminderTplForSingleApt: 'emailReminders',
    emailReminderTplForMultiApt: 'emailReminders',
    premedTpl: 'premedReminders',
    apptBookedSmsTpl: null,
    apptBookedEmailTpl: null,
    recallReminderTplForSingleApt: 'recallRemindersOn',
    recallReminderTplForMultiApt: 'recallRemindersOn',
    recallReminderEmailTplForSingleApt: 'recallRemindersOn',
    recallReminderEmailTplForMultiApt: 'recallRemindersOn',
};

// Recall-type rules (WP6): a rule's text message and email are plain messages too. The slot machinery below addresses
// a message by KEY — a slot key ('premedTpl') or a rule key 'rule:<ruleId>:<sms|email>' — and the few accessors
// that tell the two apart (_slotAt, _putSlot, _isInlineAt, _channelOf, _defaultAt) are the only places that know.
// A slot key never matches RULE_KEY_RE (cypress/node-tests/apt-reminder-inline-messages.test.js pins it). The id
// format is the shared layer's (reminderMessageDefaults.js inlineRuleId): [a-z0-9]{6,16}.
const RULE_KEY_RE = /^rule:([a-z0-9]{6,16}):(sms|email)$/;
// Which property of a rule holds each channel's template (null = that channel is off for the rule).
const RULE_TPL_PROP = { sms: 'smsTemplate', email: 'emailTemplate' };
// Rule messages follow the same switch as the four general recall boxes.
const RULE_SECTION = 'recallRemindersOn';

export default {
    name: 'AptReminderSettings',
    components: {Widget, PopupErrorDisplay, Multiselect, DatePicker, WhatsAppReminderTemplateNote, MessageTemplateBox, FollowupsUiToggle},
    mixins: [mixin],

    async created() {
        await this.init();
    },

    data(){
        return {
            activateAptReminderOn: '',
            datepickerText: "",
            allAptReminderSettings: null,
            operatoriesToIgnore: [],
            confirmationStatusToIgnore: [],
            firstMsgOdStatusEnabled: 'FALSE',
            firstMsgOdStatusSms: '',
            firstMsgOdStatusEmail: '',
            minorAge: null,
            spanishEnabled: 'FALSE',  // Spanish language support toggle
            textConfirmations: false,            
            textConfirmationTimeOfDayToSend: 9,
            textConfirmTplForSingleApt: "",
            textConfirmTplForMultiApt: "",
            textConfirmTplForAutoReply: "",
            textConfirmOne: null,
            textConfirmOneNumOfDays: null,
            textConfirmTwo: null,
            textConfirmTwoNumOfDays: null,
            textConfirmThree: null,
            textConfirmThreeNumOfDays: null,
            textConfirmKeywords: "c, ok, k, yes, y, thank, thanks, confirm, confirmed, si",
            textReminders: false,
            doNotSendIfPatientConfirmReceivedOnSameDayAsNextReminder: false,
            skipTextReminderOnceConfirmed: false,
            textReminderTimeOfDayToSend: 9,
            textReminderTplForSingleApt: '',
            textReminderTplForMultiApt: '',
            textReminderOne: null,
            textReminderOneNumOfDays: null,
            textReminderTwo: null,
            textReminderTwoNumOfDays: null,
            textSameDayReminder: "FALSE",
            textSameDayReminderHoursBefore: 1,
            textSameDayReminderEarliestHourToSend: 8,
            textSameDayReminderTplForSingleApt: '',
            textSameDayReminderTplForMultiApt: '',
            emailConfirmations: false,
            emailConfirmationTplForSingleApt: '',
            emailConfirmationTplForMultiApt: '',
            emailConfirmOne: null,
            emailConfirmOneNumOfDays: null,
            emailConfirmTwo: null,
            emailConfirmTwoNumOfDays: null,
            // Cancellation alerts default ON — the backend treats an ABSENT key as enabled
            // (only an explicit 'FALSE' opts out), so this default must match or a site that
            // opens and saves settings would silently turn its own alerts off.
            declineNotificationsEnabled: 'TRUE',
            declineNotificationEmails: '',
            emailReminders: false,
            emailReminderTplForSingleApt: '',
            emailReminderTplForMultiApt: '',
            emailReminderOne: null,
            emailReminderOneNumOfDays: null,
            emailReminderTwo: null,
            emailReminderTwoNumOfDays: null,
            premedReminders: false,
            premedReminderTimeOfDay: 8,
            premedTpl: '',
            apptBookedConfirmation: 'FALSE',
            apptBookedSms: false,
            apptBookedEmail: false,
            apptBookedSmsTpl: '',
            apptBookedEmailTpl: '',
            preferContactMethodAllowed: "FALSE",
            recallReminders: "FALSE",
            // Which leg owns recall ATTEMPT 1: 'EXE' (office computer, the default) or 'BACKEND'
            // (appt-reminder-service, honours recallReminderTimeOfDayToSend). OPERATOR-OWNED:
            // hydrated from the saved blob for DISPLAY ONLY — deliberately NOT in the save payload;
            // the backend's preserveOperatorOwnedSettings carries it through every Save.
            recallSendLeg: "EXE",
            // The office's on/off intent once the cloud owns attempt 1. NULL means the key is absent
            // from the blob (every site predates it), which resolves back to recallReminders — see
            // the recallRemindersOn computed. Declared here only so Vue tracks it before hydration;
            // the loader Vue.set()s whatever the blob actually contains.
            recallRemindersEnabled: null,
            recallReminderProviders: [],
            recallReminderTimeOfDayToSend: 8,
            recallReminderTplForSingleApt: '',
            recallReminderTplForMultiApt: '',
            recallReminderEmailTplForSingleApt: '',
            recallReminderEmailTplForMultiApt: '',
            recallReminderMaxNum: 1,
            recallReminderFollowUps: [
                {   recallType: 'Text',
                    durationType: 'days',
                    duration: 1
                }
            ],
            recallReminderMode: 'ALL',
            recallReminderTypeRules: [],
            recallReminderSendTiming: 'ON_DUE_DATE',
            recallReminderDaysBefore: 0,

            // ── Inline reminder messages (the 18 slots — 11 SMS, 7 email — as plain message boxes) ──
            // Bumped when a Super Admin flips the box / dropdown switch so the release gate is read again.
            layoutVersion: 0,
            // slot key → a short message shown on that box. slotErrors is set by Save (blank message, unknown
            // variable) and cleared as the box is edited; slotSubjectErrors is the same for an EMAIL box's subject
            // line; slotNotes holds "we couldn't read that wiki page".
            slotErrors: {},
            slotSubjectErrors: {},
            slotNotes: {},
            // slot key → true while "Switch to a plain message" is reading the wiki page.
            slotBusy: {},
            // What "Use a wiki template instead" / "Switch to a plain message" hand back, per slot: the wiki page the
            // slot held before it became a box, and the box's message while the slot is back on the dropdown.
            wikiStash: {},
            plainStash: {},
            // slot key → the English message the Spanish beside it goes with: as the server sent it, or (for Spanish
            // the office typed) the English at the moment they last typed in the Spanish box. A box whose English has
            // since been edited knows its Spanish is out of date (the frontend never computes the server's hash; it
            // only compares with what it was handed).
            englishAtLoad: {},
            // The same for an EMAIL box's English subject line (slot key → subject); SMS boxes have none.
            subjectAtLoad: {},
            // "Switch every message to a plain message": running flag + the last result ({ switched, defaulted }).
            convertBusy: false,
            convertResult: null,
            // The online-scheduling link in recall messages (plan docs/plans/2026-10-19-online-scheduling-link-token-plan.md):
            // the appointment types a recall link may open (bookableRecallAppointmentTypes of this site's online-scheduling
            // settings) and where loading them stands: 'idle' | 'loading' | 'ready' | 'failed'. Only read for an office
            // that has online scheduling. A failed read leaves the list empty: the picker then offers only "Your booking page".
            bookingTypes: [],
            bookingTypesState: 'idle'
        }
    },

    methods: {

        ...mapActions('aptreminder', ['getAptReminderSettings', 'saveAptReminderSettings', 'pauseAppointmentReminder']),
        ...mapActions('customer', ['getSettings']),
        ...mapActions('wiki', ['getWikiPages']),
        ...mapActions('onlineAppointments', ['readSettingsQuietly']),

        async init() {
            this.resetError();
            await this.getSettings({siteID: this.siteID});
            // The site settings (online scheduling or not) are in the store before anything below is built, so the boxes
            // start from the right default text. The appointment types for the booking-link picker load alongside the
            // reminder settings and never hold the page up.
            this._loadBookingTypes();
            this.allAptReminderSettings = await this.getAptReminderSettings({siteID: this.siteID});
            let temp = this.allAptReminderSettings[2];
            if(temp.length > 0) {
                let settings = JSON.parse(temp[0].PropertyValue);                
                //console.dir(settings);                
                for (var prop in settings) {
                    //console.log(`prop: ${prop}`);                    
                    Vue.set(this, prop, settings[prop]);                    
                }
            }            
            this._hydrateInlineSlots();
            this._snapshotEnglish();
        },

        async closeSlideOutPanel() {          
          this.$emit('closePanel',this.allAptReminderSettings);
        },

        // The settings blob stores each picked template as a FULL SNAPSHOT of its wiki metadata
        // (IsSmsTemplate / IsEmailTemplate / Tags / Name / SendgridTemplateID). That snapshot goes
        // STALE the moment the wiki page is re-typed later (e.g. its "SMS Template" checkbox flips
        // 0→1). The exe-side type guard (teamio-exe templateTypeGuard.js) trusts the snapshot's flags
        // and silently skips sends on mismatch. So every Save re-syncs every slot from the live
        // template list (allAptReminderSettings[3], loaded fresh from the backend on init) by
        // WikiPageID — self-healing, no manual re-pick needed.
        _refreshTemplateSlots() {
            const SLOT_KEYS = [
                'textConfirmTplForSingleApt', 'textConfirmTplForMultiApt', 'textConfirmTplForAutoReply',
                'textReminderTplForSingleApt', 'textReminderTplForMultiApt',
                'textSameDayReminderTplForSingleApt', 'textSameDayReminderTplForMultiApt',
                'emailConfirmationTplForSingleApt', 'emailConfirmationTplForMultiApt',
                'emailReminderTplForSingleApt', 'emailReminderTplForMultiApt',
                'premedTpl', 'apptBookedSmsTpl', 'apptBookedEmailTpl',
                'recallReminderTplForSingleApt', 'recallReminderTplForMultiApt',
                'recallReminderEmailTplForSingleApt', 'recallReminderEmailTplForMultiApt',
            ];
            const liveList = (this.allAptReminderSettings && this.allAptReminderSettings[3]) || [];
            SLOT_KEYS.forEach(key => {
                const slot = this[key];
                if (!slot || typeof slot !== 'object' || !slot.WikiPageID) return; // empty-string/null/no-id → untouched
                if (isInlineSlot(slot)) return; // a plain message has no wiki page to re-sync from — never touched
                const fresh = liveList.find(t => t.WikiPageID === slot.WikiPageID);
                if (fresh) this[key] = { ...fresh }; // re-typed? pull current flags/tags/name. Deleted/list-missing? leave as-is.
            });
        },

        async save() {
            this._refreshTemplateSlots();
            if(this.emailConfirmations === 'TRUE' || this.emailReminders === 'TRUE') {
                if(!this.emailVerified) {
                    this.setErrored2(true);
                    this.setMessage2("You do not have any emails verified yet. Please verify your FROM email address with teamio first by contacting us. For now, you can turn off email alerts to continue saving your settings");
                    return false;
                }
            }

            // WhatsApp-only office: a text confirmation / reminder only goes out as an approved
            // WhatsApp template — refuse to save one WhatsApp can't deliver (it would never send).
            const whatsappRefusal = reminderTextSaveRefusal(this.allSettings, {
                textConfirmations: this.textConfirmations,
                textReminders: this.textReminders,
                textSameDayReminder: this.textSameDayReminder,
            });
            if (whatsappRefusal) {
                this.setErrored2(true);
                this.setMessage2(whatsappRefusal);
                return false;
            }

            if(this.textConfirmations === 'TRUE' || this.textReminders === 'TRUE') {
                if(!this.phoneAvailable) {
                    this.setErrored2(true);
                    this.setMessage2("You do not have any TEXT Phone numbers available yet. Please contact teamio to have this set up first. For now, you can turn off TEXT alerts to continue saving your settings");
                    return false;
                }
            }

            if(this.apptBookedConfirmation === 'TRUE') {
                if(!this.apptBookedSms && !this.apptBookedEmail) {
                    this.setErrored2(true);
                    this.setMessage2("Appointment Booked Confirmation is on but no method is selected. Please enable SMS, Email, or both — or turn off Appointment Booked Confirmation.");
                    return false;
                }
                if(this.apptBookedSms && (!this.apptBookedSmsTpl || !this.apptBookedSmsTpl.WikiPageID)) {
                    this.setErrored2(true);
                    this.setMessage2("Please select an SMS template for Appointment Booked Confirmation, or turn off SMS.");
                    return false;
                }
                if(this.apptBookedEmail && (!this.apptBookedEmailTpl || !this.apptBookedEmailTpl.WikiPageID)) {
                    this.setErrored2(true);
                    this.setMessage2("Please select an Email template for Appointment Booked Confirmation, or turn off Email.");
                    return false;
                }
                if(this.apptBookedSms && this.smsOutboundOff) {
                    this.setErrored2(true);
                    this.setMessage2("Texting is turned off for this office, so the Appointment Booked Confirmation can't be sent by SMS. Turn off SMS for it (Email still works) to save your settings.");
                    return false;
                }
                if(this.apptBookedSms && !this.bookedSmsAvailable) {
                    this.setErrored2(true);
                    this.setMessage2("You do not have any TEXT Phone numbers available yet. Please contact teamio to have this set up first. For now, you can turn off SMS for Appointment Booked Confirmation to continue saving your settings");
                    return false;
                }
                if(this.apptBookedEmail && !this.emailVerified) {
                    this.setErrored2(true);
                    this.setMessage2("You do not have any emails verified yet. Please verify your FROM email address with teamio first by contacting us. For now, you can turn off Email for Appointment Booked Confirmation to continue saving your settings");
                    return false;
                }
            }

            if(this.firstMsgOdStatusEnabled === 'TRUE') {
                let smsStatusSelected = this.firstMsgOdStatusSms && this.firstMsgOdStatusSms.ODDefNum;
                let emailStatusSelected = this.firstMsgOdStatusEmail && this.firstMsgOdStatusEmail.ODDefNum;
                if(!smsStatusSelected && !emailStatusSelected) {
                    this.setErrored2(true);
                    this.setMessage2("Please select at least one Open Dental status (SMS or Email) for the first reminder/confirmation option.");
                    return false;
                }
            }

            let settingsToSave = this._buildSettingsObject();

            // A plain message must say something and use only variables we can fill in — whether or not its
            // section is switched on (a blank would be enqueued by the exe and sent as an empty text). The same
            // rule runs on the server; this just says it on the right box first.
            if (!this._validateInlineMessages(settingsToSave)) {
                return false;
            }

            let payload = {
                siteID: this.siteID,
                settings: JSON.stringify(settingsToSave)
            }
            console.log(`payload: ${JSON.stringify(payload)}`);
            const saved = await this.saveAptReminderSettings(payload);
            // A failed save resolves undefined and has already put the server's message in the banner: don't
            // toast "saved" over it, and keep the panel open so nothing typed is lost.
            if (!saved) {
                return false;
            }
            this.$toast.success('Appointment Reminder settings saved successfully!');
            // With the boxes on, the server has just made (or kept) the Spanish for each plain message: read the
            // settings back BEFORE closing, so the Appointment Reminders page and the next open show what it stored.
            // The parent ignores the panel's result, so the contract is unchanged: closePanel always fires, once.
            const fresh = this.useInlineMessages ? await this._reloadSavedSettings() : null;
            this.$emit('closePanel', fresh || settingsToSave);
        },

        // Best effort (the Save already worked): the same two reads init() makes, then the server's plain messages
        // (with their Spanish) are taken in. Resolves the settings blob as stored, or null when it could not be read.
        async _reloadSavedSettings() {
            try {
                await this.getSettings({siteID: this.siteID});
                const fresh = await this.getAptReminderSettings({siteID: this.siteID});
                const row = fresh && fresh[2] && fresh[2][0];
                if (!row || !row.PropertyValue) return null;
                const stored = JSON.parse(row.PropertyValue);
                if (!stored || typeof stored !== 'object') return null;
                this.allAptReminderSettings = fresh;
                INLINE_KEYS.forEach(key => {
                    if (isInlineSlot(stored[key])) Vue.set(this, key, stored[key]);
                });
                // The recall-type rules' plain messages (with the Spanish the server made): matched to the rules on
                // screen by rule id and put into the SAME rule objects, so the cards are not rebuilt.
                if (Array.isArray(stored.recallReminderTypeRules) && Array.isArray(this.recallReminderTypeRules)) {
                    stored.recallReminderTypeRules.forEach(savedRule => {
                        if (!savedRule || typeof savedRule !== 'object' || !savedRule.id) return;
                        const rule = this.recallReminderTypeRules.find(r => r && r.id === savedRule.id);
                        if (!rule) return;
                        ['sms', 'email'].forEach(channel => {
                            const prop = RULE_TPL_PROP[channel];
                            if (isInlineRuleTemplate(savedRule[prop]) && isInlineRuleTemplate(rule[prop])) Vue.set(rule, prop, savedRule[prop]);
                        });
                    });
                }
                this._snapshotEnglish();
                return stored;
            } catch (err) {
                console.log('Could not read the saved reminder settings back:', err);
                return null;
            }
        },

        showModal(name) {            
            this.$refs[name].show();
        },
        
        hideModal(ref) {
            this.$refs[ref].hide();
        },

        _buildSettingsObject() {
            const settings = {
                activateAptReminderOn: this.activateAptReminderOn,
                operatoriesToIgnore: this.operatoriesToIgnore,
                confirmationStatusToIgnore: this.confirmationStatusToIgnore,
                firstMsgOdStatusEnabled: this.firstMsgOdStatusEnabled,
                firstMsgOdStatusSms: this.firstMsgOdStatusSms,
                firstMsgOdStatusEmail: this.firstMsgOdStatusEmail,
                minorAge: this.minorAge,
                spanishEnabled: this.spanishEnabled,
                textConfirmations: this.textConfirmations,
                textConfirmationTimeOfDayToSend: this.textConfirmationTimeOfDayToSend,
                textConfirmTplForSingleApt: this.textConfirmTplForSingleApt,
                textConfirmTplForMultiApt: this.textConfirmTplForMultiApt,
                textConfirmTplForAutoReply: this.textConfirmTplForAutoReply,
                textConfirmOne: this.textConfirmOne,
                textConfirmOneNumOfDays: this.textConfirmOneNumOfDays,
                textConfirmTwo: this.textConfirmTwo,
                textConfirmTwoNumOfDays: this.textConfirmTwoNumOfDays,
                textConfirmThree: this.textConfirmThree,
                textConfirmThreeNumOfDays: this.textConfirmThreeNumOfDays,
                textConfirmKeywords: this.textConfirmKeywords,
                textReminders: this.textReminders,
                doNotSendIfPatientConfirmReceivedOnSameDayAsNextReminder: this.doNotSendIfPatientConfirmReceivedOnSameDayAsNextReminder,
                skipTextReminderOnceConfirmed: this.skipTextReminderOnceConfirmed,
                textReminderTimeOfDayToSend: this.textReminderTimeOfDayToSend,
                textReminderTplForSingleApt: this.textReminderTplForSingleApt,
                textReminderTplForMultiApt: this.textReminderTplForMultiApt,
                textReminderOne: this.textReminderOne,
                textReminderOneNumOfDays: this.textReminderOneNumOfDays,
                textReminderTwo: this.textReminderTwo,
                textReminderTwoNumOfDays: this.textReminderTwoNumOfDays,
                textSameDayReminder: this.textSameDayReminder,
                textSameDayReminderHoursBefore: this.textSameDayReminderHoursBefore,
                textSameDayReminderEarliestHourToSend: this.textSameDayReminderEarliestHourToSend,
                textSameDayReminderTplForSingleApt: this.textSameDayReminderTplForSingleApt,
                textSameDayReminderTplForMultiApt: this.textSameDayReminderTplForMultiApt,
                emailConfirmations: this.emailConfirmations,
                emailConfirmationTplForSingleApt: this.emailConfirmationTplForSingleApt,
                emailConfirmationTplForMultiApt: this.emailConfirmationTplForMultiApt,
                emailConfirmOne: this.emailConfirmOne,
                emailConfirmOneNumOfDays: this.emailConfirmOneNumOfDays,
                emailConfirmTwo: this.emailConfirmTwo,
                emailConfirmTwoNumOfDays: this.emailConfirmTwoNumOfDays,
                declineNotificationsEnabled: this.declineNotificationsEnabled,
                declineNotificationEmails: this.declineNotificationEmails,
                emailReminders: this.emailReminders,
                emailReminderTplForSingleApt: this.emailReminderTplForSingleApt,
                emailReminderTplForMultiApt: this.emailReminderTplForMultiApt,
                emailReminderOne: this.emailReminderOne,
                emailReminderOneNumOfDays: this.emailReminderOneNumOfDays,
                emailReminderTwo: this.emailReminderTwo,
                emailReminderTwoNumOfDays: this.emailReminderTwoNumOfDays,
                premedReminders: this.premedReminders,
                premedReminderTimeOfDay: this.premedReminderTimeOfDay,
                premedTpl: this.premedTpl,
                apptBookedConfirmation: this.apptBookedConfirmation,
                apptBookedSms: this.apptBookedSms,
                apptBookedEmail: this.apptBookedEmail,
                apptBookedSmsTpl: this.apptBookedSmsTpl,
                apptBookedEmailTpl: this.apptBookedEmailTpl,
                preferContactMethodAllowed: this.preferContactMethodAllowed,
                recallReminders: this.recallReminders,
                /*
                    Sent ONLY when it already exists on this site. ⛔ Writing it unconditionally
                    would persist recallRemindersEnabled:'FALSE' onto every un-migrated practice
                    (the switch never touches it there, so it would keep its null-ish default) — and
                    the day that site was migrated, the resolver would read the stale 'FALSE' and
                    silently switch recall OFF. Absent must stay absent until retirement writes it.
                */
                ...(this.recallRemindersEnabled === null || this.recallRemindersEnabled === undefined
                    ? {}
                    : { recallRemindersEnabled: this.recallRemindersEnabled }),
                recallReminderProviders: this.recallReminderProviders,
                recallReminderTimeOfDayToSend: this.recallReminderTimeOfDayToSend,
                recallReminderTplForSingleApt: this.recallReminderTplForSingleApt,
                recallReminderTplForMultiApt: this.recallReminderTplForMultiApt,
                recallReminderEmailTplForSingleApt: this.recallReminderEmailTplForSingleApt,
                recallReminderEmailTplForMultiApt: this.recallReminderEmailTplForMultiApt,
                recallReminderMaxNum: this.recallReminderMaxNum,
                recallReminderFollowUps: this.recallReminderFollowUps,
                recallReminderMode: this.recallReminderMode,
                recallReminderTypeRules: this.recallReminderTypeRules,
                recallReminderSendTiming: this.recallReminderSendTiming,
                recallReminderDaysBefore: this.recallReminderDaysBefore,
            };
            this._writeInlineSlots(settings);
            return settings;
        },

        // With the message boxes on, each box slot is written as a normalized inline slot (buildInlineSlot) holding
        // the text in the box. Wiki and empty slots — and EVERY slot while the dropdowns are showing — are written
        // exactly as they are, so the classic screen saves exactly what it always did.
        _writeInlineSlots(settings) {
            if (!this.useInlineMessages) return;
            INLINE_KEYS.forEach(key => {
                if (!isInlineSlot(this[key])) return;
                const slot = this._inlineSlotWithText(key, this.inlineTextOf(key));
                this._clearEmptiedSpanish(slot.inline);
                settings[key] = slot;
            });
            // The recall-type rules: the same for each rule's plain messages, rebuilt from the RULE's own id so the
            // template id can never disagree with it. A COPY of the list — the live rules are not rewritten — and a
            // wiki channel, an off channel and the rest of the rule go out exactly as they are.
            if (Array.isArray(settings.recallReminderTypeRules)) {
                settings.recallReminderTypeRules = settings.recallReminderTypeRules.map(rule => {
                    if (!rule || typeof rule !== 'object') return rule;
                    const out = Object.assign({}, rule);
                    ['sms', 'email'].forEach(channel => {
                        const prop = RULE_TPL_PROP[channel];
                        const key = this.ruleKeyOf(rule, channel);
                        if (!key || !isInlineRuleTemplate(rule[prop])) return;
                        const tpl = this._inlineSlotWithText(key, this.inlineTextOf(key));
                        this._clearEmptiedSpanish(tpl.inline);
                        out[prop] = tpl;
                    });
                    return out;
                });
            }
        },

        // Hand-edited Spanish that the office then emptied is no Spanish at all. Left as "edited" the server would
        // never translate it again (and patients would get English), so it goes back to automatic (an email's
        // Spanish subject goes with it). Works on a message's `inline` object, slot or rule.
        _clearEmptiedSpanish(inline) {
            if (inline.spaEdited === true && String(inline.textSpa || '').trim() === '') {
                inline.spaEdited = false;
                inline.textSpa = '';
                if (Object.prototype.hasOwnProperty.call(inline, 'subjectSpa')) inline.subjectSpa = '';
                delete inline.hash;
                delete inline.spaStale; // same as Re-translate: no longer hand-edited Spanish
            }
        },

        // ── Inline reminder messages (the 18 slots: 11 SMS, 7 email) ─────────────────────────────────

        // The slot as a plain message holding `text` (and, on an email slot, `subject`). buildInlineSlot owns the
        // shape; whatever else the stored inline object carries (the Spanish text and its bookkeeping, added by the
        // server) is kept, so editing the English here never drops a hand-edited translation. An email slot keeps its
        // CURRENT subject unless one is passed — editing the body must never put the default subject back.
        _inlineSlotWithText(key, text, subject) {
            const current = this._slotAt(key);
            const kept = this._isInlineAt(key) && current.inline && typeof current.inline === 'object' ? current.inline : {};
            const opts = { text };
            if (subject !== undefined) opts.subject = subject;
            else if (typeof kept.subject === 'string') opts.subject = kept.subject;
            const rule = RULE_KEY_RE.exec(key);
            if (!rule) {
                const slot = buildInlineSlot(key, opts);
                slot.inline = Object.assign({}, kept, slot.inline);
                return slot;
            }
            // A recall-type rule's message: same shape, built from the rule's id. buildInlineRuleTemplate refills a
            // BLANK message with the default, so the typed value is put back — a cleared box stays cleared (and Save
            // refuses it), it is never silently refilled while the office is typing.
            const channel = rule[2];
            const tpl = buildInlineRuleTemplate(rule[1], channel, opts);
            tpl.inline = Object.assign({}, kept, tpl.inline, { text });
            if (channel === 'email' && opts.subject !== undefined) tpl.inline.subject = opts.subject;
            return tpl;
        },

        // An empty slot has nothing to send today. With the boxes on it starts as the default message (and, for an
        // email, the default subject), so what the office reads in the box is what Save stores and what patients
        // get. Wiki slots are left alone.
        _hydrateInlineSlots() {
            if (!this.useInlineMessages) return;
            INLINE_KEYS.forEach(key => {
                const slot = this[key];
                const empty = !slot || typeof slot !== 'object' || !slot.WikiPageID;
                // The default for this office (the recall text has a "book online" version for an office with online scheduling).
                if (empty) this[key] = buildInlineSlot(key, { text: this._pickDefault(getSlotDefault(key)) });
            });
        },

        // This slot is drawn as a message box (the release gate says so AND it holds a plain message).
        slotIsBox(key) {
            return this.useInlineMessages && this._isInlineAt(key);
        },

        inlineTextOf(key) {
            const slot = this._slotAt(key);
            return this._isInlineAt(key) && slot.inline && typeof slot.inline.text === 'string' ? slot.inline.text : '';
        },

        // An email box's subject line ('' for an SMS box, which has none).
        inlineSubjectOf(key) {
            const slot = this._slotAt(key);
            return this._isInlineAt(key) && slot.inline && typeof slot.inline.subject === 'string' ? slot.inline.subject : '';
        },

        // Is the section this slot belongs to switched on? (An off section shows its boxes read-only.) A rule's
        // messages follow the recall switch, like the four general recall boxes.
        sectionOn(key) {
            const prop = this._isRuleKey(key) ? RULE_SECTION : SLOT_SECTION[key];
            return !prop || String(this[prop]) === 'TRUE';
        },

        // ── Messages are addressed by KEY: a slot key, or a rule key 'rule:<ruleId>:<sms|email>' ─────────────
        // These accessors are the only code that knows the difference, so the Spanish / switch / validation
        // machinery below serves the 18 slots and every recall-type rule message alike.

        _isRuleKey(key) {
            return typeof key === 'string' && RULE_KEY_RE.test(key);
        },

        // The rule a rule key names ({ rule, channel, prop }), or null when the key is not a rule key or the rule is gone.
        _ruleRef(key) {
            const m = typeof key === 'string' ? RULE_KEY_RE.exec(key) : null;
            if (!m) return null;
            const rules = Array.isArray(this.recallReminderTypeRules) ? this.recallReminderTypeRules : [];
            const rule = rules.find(r => r && typeof r === 'object' && r.id === m[1]);
            return rule ? { rule, channel: m[2], prop: RULE_TPL_PROP[m[2]] } : null;
        },

        // The slot object / rule template a key names (a rule that is gone reads as nothing).
        _slotAt(key) {
            if (this._isRuleKey(key)) {
                const ref = this._ruleRef(key);
                return ref ? ref.rule[ref.prop] : null;
            }
            return this[key];
        },

        _putSlot(key, value) {
            if (this._isRuleKey(key)) {
                const ref = this._ruleRef(key);
                if (ref) Vue.set(ref.rule, ref.prop, value);
                return;
            }
            this[key] = value;
        },

        // Does this key hold a plain message?
        _isInlineAt(key) {
            return this._isRuleKey(key) ? isInlineRuleTemplate(this._slotAt(key)) : isInlineSlot(this[key]);
        },

        _channelOf(key) {
            const m = typeof key === 'string' ? RULE_KEY_RE.exec(key) : null;
            return m ? m[2] : (getSlotDefault(key) || {}).channel;
        },

        // The default message entry for a key. A rule starts from the recall SINGLE default of its channel (and
        // shares that slot's helper line).
        _defaultAt(key) {
            const m = typeof key === 'string' ? RULE_KEY_RE.exec(key) : null;
            if (!m) return getSlotDefault(key);
            return Object.assign({}, getRuleDefault(m[2]), { helper: (getSlotDefault(RULE_CHANNEL_SLOT[m[2]]) || {}).helper || '' });
        },

        // What "no message here" is: '' for a slot, null for a rule channel (null = that channel is off).
        _emptyAt(key) {
            return this._isRuleKey(key) ? null : '';
        },

        // The rule keys of every rule channel that holds a plain message, and every message key there is.
        _ruleKeys() {
            const keys = [];
            (Array.isArray(this.recallReminderTypeRules) ? this.recallReminderTypeRules : []).forEach(rule => {
                ['sms', 'email'].forEach(channel => {
                    const key = this.ruleKeyOf(rule, channel);
                    if (key && isInlineRuleTemplate(rule[RULE_TPL_PROP[channel]])) keys.push(key);
                });
            });
            return keys;
        },

        _messageKeys() {
            return INLINE_KEYS.concat(this._ruleKeys());
        },

        // Everything the box needs for one slot; the label is the same wording the dropdown had.
        boxProps(key, label) {
            const def = getSlotDefault(key) || {};
            const isEmail = def.channel === 'email';
            const props = {
                label,
                slotKey: key,
                channel: def.channel || 'sms',
                value: this.inlineTextOf(key),
                defaultText: this._pickDefault(def),
                chips: def.chips ? def.chips.slice() : [],
                helper: def.helper || '',
                error: this.slotErrors[key] || '',
                note: this.slotNotes[key] || '',
                // Section off ⇒ read-only. WhatsApp-only office ⇒ the TEXT is not what is sent (the note above the
                // sections says what is), so the text boxes are for reference. Email still goes out there.
                disabled: !this.sectionOn(key),
                informational: !isEmail && !!this.smsOutboundOff,
                wikiLink: true,
                spanish: this.spanishFor(key),
                booking: this.bookingProps(key),
            };
            if (isEmail) {
                props.subject = this.inlineSubjectOf(key);
                props.defaultSubject = def.subject || '';
                props.subjectError = this.slotSubjectErrors[key] || '';
                props.previewSite = this.emailPreviewSite;
            }
            return props;
        },

        setInlineText(key, text) {
            this._putSlot(key, this._inlineSlotWithText(key, text));
            if (this.slotErrors[key]) {
                const { [key]: _gone, ...rest } = this.slotErrors;
                this.slotErrors = rest;
            }
        },

        // The email box's Subject input. The message and the Spanish are carried over untouched.
        setInlineSubject(key, subject) {
            this._putSlot(key, this._inlineSlotWithText(key, this.inlineTextOf(key), subject));
            if (this.slotSubjectErrors[key]) {
                const { [key]: _gone, ...rest } = this.slotSubjectErrors;
                this.slotSubjectErrors = rest;
            }
        },

        // ── The online-scheduling link in recall messages (docs/plans/2026-10-19-online-scheduling-link-token-plan.md) ──
        // The four recall slots and every recall-type rule message may hold {{online_scheduling_link}}; the message also
        // stores WHICH booking page it opens, beside the text: inline.bookingLink = { aptTypeId, label } (null aptTypeId =
        // the general booking page). teamio-utility reads exactly that field at send time. Offices without online
        // scheduling never see the chip, and a box that still holds the token refuses Save (_validateInlineMessages).

        // The default message for THIS office: the recall text's "book online" version when it has online scheduling, the phone
        // version otherwise (reminderMessageDefaults pickDefaultText). init() reads the site settings before anything is
        // built, so no box starts on the wrong version and none needs re-filling when the status arrives.
        _pickDefault(def) {
            return pickDefaultText(def || {}, { hasOnlineScheduling: this.hasOnlineScheduling }) || '';
        },

        // May this key's message hold {{online_scheduling_link}}? The four recall slots, and every recall-type rule message.
        _allowsBookingLink(key) {
            return this._isRuleKey(key) || slotAllowsBookingLink(key);
        },

        // The saved choice of booking page for a message: { aptTypeId, label } (no choice saved = the general booking page).
        bookingLinkOf(key) {
            const slot = this._slotAt(key);
            const saved = this._isInlineAt(key) && slot.inline && slot.inline.bookingLink && typeof slot.inline.bookingLink === 'object' ? slot.inline.bookingLink : {};
            const id = typeof saved.aptTypeId === 'string' && saved.aptTypeId.trim() !== '' ? saved.aptTypeId : null;
            return { aptTypeId: id, label: id !== null && typeof saved.label === 'string' ? saved.label : '' };
        },

        // What a box needs for the link chip / popover / readout (MessageTemplateBox `booking` prop).
        bookingProps(key) {
            return {
                allowed: this._allowsBookingLink(key),
                available: !!this.hasOnlineScheduling,
                link: this.bookingLinkOf(key),
                types: this.bookingTypes,
                typesLoaded: this.bookingTypesState === 'ready',
                typesLoading: this.bookingTypesState === 'loading',
            };
        },

        // The popover's pick. The text, the Spanish and everything else on the message are carried over untouched.
        setBookingLink(key, link) {
            if (!this._allowsBookingLink(key) || !this._isInlineAt(key)) return;
            const id = link && typeof link.aptTypeId === 'string' && link.aptTypeId.trim() !== '' ? link.aptTypeId : null;
            const slot = this._inlineSlotWithText(key, this.inlineTextOf(key));
            slot.inline.bookingLink = { aptTypeId: id, label: id !== null && typeof link.label === 'string' ? link.label : '' };
            this._putSlot(key, slot);
        },

        // This site's appointment types for the picker, read once through a store action that touches no shared state
        // (the Online Appointments page's data is left alone). Only for an office that has online scheduling and shows the
        // boxes. Never throws and never holds the page up: a failed read logs, leaves the list empty (the picker then
        // offers only "Your booking page") and can be tried again. Resolves when the read has finished.
        _loadBookingTypes() {
            if (!this.useInlineMessages || !this.hasOnlineScheduling) return Promise.resolve();
            if (this.bookingTypesState === 'ready') return Promise.resolve();
            if (this._bookingTypesLoad) return this._bookingTypesLoad;
            const siteID = this.siteID;
            this.bookingTypesState = 'loading';
            this._bookingTypesLoad = (async () => {
                try {
                    const settings = await this.readSettingsQuietly({ siteID });
                    if (siteID !== this.siteID) { this.bookingTypesState = 'idle'; return; } // another office meanwhile
                    this.bookingTypes = bookableRecallAppointmentTypes(settings);
                    this.bookingTypesState = 'ready';
                } catch (err) {
                    console.log('Could not read the appointment types for the online scheduling link:', err);
                    this.bookingTypes = [];
                    this.bookingTypesState = 'failed';
                } finally {
                    this._bookingTypesLoad = null;
                }
            })();
            return this._bookingTypesLoad;
        },

        // How "Switch to a plain message" turns a wiki page's links into text for this message (wikiPlainText.js LINKS note):
        // a booking-page link becomes the online-scheduling token only where the message may hold it AND the office has online
        // scheduling (else Save would refuse it) - it waits for the appointment types so the choice can be checked.
        async _plainTextOptions(key) {
            const allowBookingLink = !!this.hasOnlineScheduling && this._allowsBookingLink(key);
            if (allowBookingLink) await this._loadBookingTypes();
            return {
                allowBookingLink,
                origin: typeof window !== 'undefined' && window.location ? window.location.origin : '',
                aptTypes: this.bookingTypes,
            };
        },

        // The booking page a converted wiki link opens, as the message stores it. The wiki page may belong to another
        // office (a host template), so an appointment type that is not among THIS office's bookable types - or any type at
        // all while the list could not be read - becomes the general booking page.
        _bookableLinkChoice(link) {
            const id = link && typeof link.aptTypeId === 'string' ? link.aptTypeId : null;
            const type = id !== null && this.bookingTypesState === 'ready' ? this.bookingTypes.find(t => t.id === id) : null;
            return type ? { aptTypeId: type.id, label: type.name || '' } : { aptTypeId: null, label: '' };
        },

        // ── Spanish version of a plain message (plan §0 D7) ───────────────────────────────────────────
        // The server makes it on Save (only while "Send messages in Spanish" is on) and stores it in the slot beside
        // the English: inline.textSpa, inline.spaEdited, inline.hash. This screen never makes or hashes Spanish; it
        // shows what the server stored, lets the office edit it, and hands it all back untouched.

        // The English of every plain message as the server sent it, taken when the settings are read.
        _snapshotEnglish() {
            const seen = {};
            const seenSubjects = {};
            this._messageKeys().forEach(key => {
                if (!this._isInlineAt(key)) return;
                seen[key] = this.inlineTextOf(key);
                if (this._channelOf(key) === 'email') seenSubjects[key] = this.inlineSubjectOf(key);
            });
            this.englishAtLoad = seen;
            this.subjectAtLoad = seenSubjects;
        },

        // What the box shows for its Spanish. English edited after the Spanish was made:
        //   - Spanish the server made (spaEdited false) is out of date: not shown, the box says it is created on Save
        //     (the server translates the changed English; editing the English back brings the old Spanish back);
        //   - Spanish the office wrote (spaEdited true) is kept as it is, with a note and the Re-translate link.
        spanishFor(key) {
            const slot = this._slotAt(key);
            const inline = this._isInlineAt(key) && slot.inline && typeof slot.inline === 'object' ? slot.inline : {};
            const text = typeof inline.textSpa === 'string' ? inline.textSpa : '';
            const edited = inline.spaEdited === true;
            const was = this.englishAtLoad ? this.englishAtLoad[key] : undefined;
            const isEmail = this._channelOf(key) === 'email';
            // An email's English is its subject AND its body: a change to either makes the Spanish out of date.
            const wasSubject = isEmail && this.subjectAtLoad ? this.subjectAtLoad[key] : undefined;
            const englishChanged = (was !== undefined && was !== this.inlineTextOf(key))
                || (wasSubject !== undefined && wasSubject !== this.inlineSubjectOf(key));
            const subjectSpa = typeof inline.subjectSpa === 'string' ? inline.subjectSpa : '';
            // The server makes the Spanish subject together with the Spanish body, so one without the other is not usable.
            const pending = !edited && (text.trim() === '' || (isEmail && subjectSpa.trim() === '') || englishChanged);
            // The server flags hand-edited Spanish whose English changed since (inline.spaStale, worked out on every
            // Save / Pause; absent or false otherwise). It only ever speaks for Spanish the office wrote, and it covers
            // English changes from EARLIER sessions that this page cannot see. Either signal shows the note.
            const serverStale = edited && inline.spaStale === true;
            const out = {
                enabled: this.spanishEnabled === 'TRUE',
                text: pending ? '' : text,
                edited,
                pending,
                englishChanged: edited && (englishChanged || serverStale),
            };
            if (isEmail) out.subject = pending ? '' : subjectSpa;
            return out;
        },

        // The office typed in the Spanish box: it is theirs now, and the server must not overwrite it. Their Spanish
        // now matches the English they can see, so any "English changed since you edited the Spanish" note clears
        // until the English is edited again:
        //   - the stored hash and the server's spaStale flag are dropped (the server stamps a missing hash with the
        //     CURRENT English on Save, so an office that updates its Spanish after changing the English is no longer
        //     flagged), and
        //   - the in-session baseline moves to the English as it is right now.
        setSpanishText(key, text) {
            const english = this.inlineTextOf(key);
            const slot = this._inlineSlotWithText(key, english);
            slot.inline.textSpa = text;
            slot.inline.spaEdited = true;
            delete slot.inline.hash;
            delete slot.inline.spaStale;
            this._putSlot(key, slot);
            this._rebaseEnglish(key);
        },

        // The email box's Spanish SUBJECT typed by the office: the same rule as the Spanish body (the server's one
        // spaEdited flag covers the whole slot, so the Spanish body is now theirs too and is never overwritten).
        setSpanishSubject(key, subjectSpa) {
            const slot = this._inlineSlotWithText(key, this.inlineTextOf(key));
            slot.inline.subjectSpa = subjectSpa;
            slot.inline.spaEdited = true;
            delete slot.inline.hash;
            delete slot.inline.spaStale;
            this._putSlot(key, slot);
            this._rebaseEnglish(key);
        },

        // Their Spanish now goes with the English (message and, for an email, subject) as it is right now.
        _rebaseEnglish(key) {
            this.englishAtLoad = { ...this.englishAtLoad, [key]: this.inlineTextOf(key) };
            if (this._channelOf(key) === 'email') {
                this.subjectAtLoad = { ...this.subjectAtLoad, [key]: this.inlineSubjectOf(key) };
            }
        },

        // "Re-translate from English": back to automatic. The server translates on the next Save; the stored hash is
        // dropped so it cannot decide the (unchanged) English needs nothing, which would leave the Spanish empty.
        // (An email's Spanish subject is emptied with the body.)
        retranslateSpanish(key) {
            const slot = this._inlineSlotWithText(key, this.inlineTextOf(key));
            slot.inline.textSpa = '';
            if (this._channelOf(key) === 'email') slot.inline.subjectSpa = '';
            slot.inline.spaEdited = false;
            delete slot.inline.hash;
            delete slot.inline.spaStale; // the server's "English changed since you edited it" flag has nothing left to say
            this._putSlot(key, slot);
        },

        // "Switch to a plain message" is offered on a wiki slot, in an editable section. A TEXT slot also needs an
        // office that texts; an EMAIL slot does not (email still goes out when texting is off).
        canSwitchToPlain(key) {
            const isEmail = this._channelOf(key) === 'email';
            return this.useInlineMessages && !this._isInlineAt(key) && this.sectionOn(key) && (isEmail || !this.smsOutboundOff);
        },

        // The wiki page's message as plain text, through an existing store action (no new endpoint). Resolves
        // { ok:false } on any failure — never throws, never touches the page-level error banner. For an EMAIL slot
        // (`asEmail`) the body keeps its paragraphs apart and loses the signature token, and the page's own
        // EmailSubject comes back as `subject` — else the page Name, which is what wiki-service gives the hosted template
        // as its subject (wiki.js SendGridCreateTemplate), so the patient-facing subject does not change. `convert` =
        // how the page's links become text (_plainTextOptions): the booking-page link keeps working as the token.
        async _readWikiPagePlainText(slot, asEmail, convert) {
            try {
                const data = await this.getWikiPages({ siteID: this.siteID, searchTerm: slot.Name, silent: true });
                const pages = (data && data[0]) || [];
                const page = pages.find(p => p.WikiPageID === slot.WikiPageID);
                if (!page) return { ok: false, text: '' };
                if (!asEmail) return wikiContentToPlainText(page.Content, convert);
                const oneLine = v => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
                return Object.assign({}, wikiContentToEmailText(page.Content, convert), {
                    subject: oneLine(page.EmailSubject) || oneLine(page.Name),
                });
            } catch (err) {
                console.log('Could not read the wiki page for a plain message:', err);
                return { ok: false, text: '' };
            }
        },

        // Wiki page → plain message, one slot at a time and reversible. The box starts as the page's own wording;
        // if the page cannot be read it starts as the default and says so.
        async switchToPlain(key) {
            if (this.slotBusy[key]) return;
            const current = this._slotAt(key);
            const page = current && typeof current === 'object' && current.WikiPageID && !this._isInlineAt(key) ? current : null;

            // Straight back to the same page they just left? Hand back the message they were editing.
            const lastPlain = this.plainStash[key];
            const lastPage = this.wikiStash[key];
            if (lastPlain && page && lastPage && lastPage.WikiPageID === page.WikiPageID) {
                this.wikiStash = { ...this.wikiStash, [key]: page };
                this._putSlot(key, lastPlain);
                return;
            }

            const def = this._defaultAt(key) || {};
            const isEmail = def.channel === 'email';
            let text = this._pickDefault(def);
            // An email starts with the page's own subject (EmailSubject, else its Name), else the slot's default subject.
            let subject = isEmail ? def.subject : undefined;
            let note = '';
            let bookingLink = null;
            if (page) {
                this.slotBusy = { ...this.slotBusy, [key]: true };
                let read;
                try {
                    const convert = await this._plainTextOptions(key);
                    read = await this._readWikiPagePlainText(page, isEmail, convert);
                } finally {
                    this.slotBusy = { ...this.slotBusy, [key]: false };
                }
                if (read.ok) {
                    text = read.text;
                    if (isEmail && read.subject) subject = read.subject;
                    // A booking-page link in the page became the token: remember which booking page it opened.
                    if (read.bookingLink) bookingLink = this._bookableLinkChoice(read.bookingLink);
                    if (read.hadFormOrPaymentLink) note = 'This page had a form or payment link that a plain message can\'t carry — keep using the wiki page for it.';
                } else note = `We couldn’t read the message in “${page.Name}”, so this box started from the default message.`;
                this.wikiStash = { ...this.wikiStash, [key]: page };
            }
            const slot = this._inlineSlotWithText(key, text, subject);
            if (bookingLink) slot.inline.bookingLink = bookingLink;
            this._putSlot(key, slot);
            this.slotNotes = { ...this.slotNotes, [key]: note };
        },

        // Plain message → back to the wiki dropdown, restoring the page that was selected before (if any). With no
        // page to go back to a slot becomes the empty dropdown ('') and a recall-type rule channel goes off (null).
        useWikiTemplate(key) {
            this.plainStash = { ...this.plainStash, [key]: this._slotAt(key) };
            this._putSlot(key, this.wikiStash[key] || this._emptyAt(key));
            this.slotNotes = { ...this.slotNotes, [key]: '' };
            if (this.slotErrors[key]) {
                const { [key]: _gone, ...rest } = this.slotErrors;
                this.slotErrors = rest;
            }
            if (this.slotSubjectErrors[key]) {
                const { [key]: _gone, ...rest } = this.slotSubjectErrors;
                this.slotSubjectErrors = rest;
            }
        },

        // Save-time check of every plain message in the blob about to be saved. Marks the boxes that fail, shows the
        // first problem in the page banner (the same banner every other Save check uses) and scrolls to that box.
        _validateInlineMessages(blob) {
            this.slotErrors = {};
            this.slotSubjectErrors = {};
            // On-screen order: the 18 slots in the order of the defaults, then the recall-type rules (rule by rule,
            // the text message before the email, the subject before the message).
            const ruleRank = (index, channel, field) => 1000 + index * 10 + (channel === 'email' ? 5 : 0) + (field === 'subject' ? 0 : (field === 'text' ? 1 : 2));
            // What a box says under itself for each kind of layer problem (any other problem on a message field is a blank one).
            const LINK_NOT_ALLOWED_BOX = 'The online scheduling link can only be used in recall messages.';
            const LINK_IN_SUBJECT_BOX = "The online scheduling link can't go in the subject line — put it in the message.";
            const LINK_NOT_SET_UP = "Online scheduling isn't set up for this office — remove the link or set up Online Scheduling first.";
            // The layer rule reports a blank message (field 'text') and, for an email, a blank subject (field 'subject'), and
            // the online-scheduling link where it may not be ('text' / 'subject', and their Spanish 'textSpa' / 'subjectSpa').
            // For a recall-type rule it also names the rule (ruleIndex, ruleId, channel): that lands on the rule's own
            // box. A problem with the rule's id itself (field 'id') cannot be fixed by typing, so it has no box.
            // A problem in the SPANISH fields follows the Spanish convention of this page (below): field 'spanish', no outline
            // on the English box (the Spanish section of the box says it and opens itself); `scrollField` lands the cursor in it.
            const problems = validateInlineSlots(blob).map(p => {
                const isRule = p.key === 'recallReminderTypeRules';
                const isSpanish = p.field === 'textSpa' || p.field === 'subjectSpa';
                const onBox = !isRule || (!!p.ruleId && !!p.channel && (p.field === 'text' || p.field === 'subject' || isSpanish));
                let boxMessage = p.field === 'subject' ? 'A subject line is required.' : 'This message is required.';
                if (p.code === 'INLINE_BOOKING_LINK_NOT_ALLOWED') boxMessage = LINK_NOT_ALLOWED_BOX;
                else if (p.code === 'INLINE_BOOKING_LINK_IN_SUBJECT') boxMessage = LINK_IN_SUBJECT_BOX;
                return {
                    key: isRule ? (onBox ? `rule:${p.ruleId}:${p.channel}` : '') : p.key,
                    field: isSpanish ? 'spanish' : p.field,
                    scrollField: isSpanish ? p.field : undefined,
                    message: p.message,
                    boxMessage: !onBox || isSpanish ? '' : boxMessage,
                    rank: isRule ? ruleRank(p.ruleIndex, p.channel, isSpanish ? 'spanish' : p.field) : INLINE_KEYS.indexOf(p.key),
                };
            });
            if (this.useInlineMessages) {
                // Every plain message in the blob: the slots, then each rule's channels. `allowsLink`: the message may hold the
                // online-scheduling token (the four recall slots, every recall-type rule message) - elsewhere it is "unknown".
                const entries = [];
                INLINE_KEYS.forEach(key => {
                    const slot = blob[key];
                    if (!isInlineSlot(slot)) return;
                    const def = getSlotDefault(key) || {};
                    entries.push({ key, label: def.label, channel: def.channel, inline: slot.inline || {}, allowsLink: slotAllowsBookingLink(key), rank: () => INLINE_KEYS.indexOf(key) });
                });
                (Array.isArray(blob.recallReminderTypeRules) ? blob.recallReminderTypeRules : []).forEach((rule, index) => {
                    ['sms', 'email'].forEach(channel => {
                        const tpl = rule && typeof rule === 'object' ? rule[RULE_TPL_PROP[channel]] : null;
                        const key = this.ruleKeyOf(rule, channel);
                        if (!key || !isInlineRuleTemplate(tpl)) return;
                        entries.push({
                            key,
                            label: `Recall type rule ${index + 1} – ${channel === 'email' ? 'email' : 'text message'}`,
                            channel,
                            inline: tpl.inline || {},
                            allowsLink: true,
                            rank: field => ruleRank(index, channel, field),
                        });
                    });
                });
                entries.forEach(({ key, label, channel, inline, allowsLink, rank }) => {
                    // A field already reported as blank is not checked again; the OTHER field of an email still is.
                    const checkVariables = (field, value, where) => {
                        if (problems.some(p => p.key === key && p.field === field)) return;
                        // The online-scheduling token is known in the message of an allowed box only (never a subject).
                        const unknown = findUnknownVariables(value, { allowBookingLink: allowsLink && field === 'text' });
                        if (!unknown.length) return;
                        const u = unknown[0];
                        const boxMessage = u.reason === 'whitespace'
                            ? `{{${u.name}}} has extra spaces inside the braces — use {{${u.suggestion}}}.`
                            : `{{${u.name}}} isn’t a variable we can fill in, so patients would see it exactly as written.${u.suggestion ? ` Did you mean {{${u.suggestion}}}?` : ''}`;
                        problems.push({ key, field, message: `${label}${where}: ${boxMessage}`, boxMessage, rank: rank(field) });
                    };
                    checkVariables('text', inline.text, '');
                    if (channel === 'email') checkVariables('subject', inline.subject, ' (subject line)');
                });
                // An office WITHOUT online scheduling cannot send the link (the sender would swap it for the office phone), so a
                // message that still holds the token - English, or hand-written Spanish - refuses Save with its own words. (A
                // message that may not hold it at all was already reported by the layer rule above.)
                if (!this.hasOnlineScheduling) {
                    entries.forEach(({ key, label, inline, allowsLink, rank }) => {
                        if (!allowsLink) return;
                        if (hasBookingLinkToken(inline.text)) {
                            if (problems.some(p => p.key === key && p.field === 'text')) return;
                            problems.push({ key, field: 'text', message: `${label}: ${LINK_NOT_SET_UP}`, boxMessage: LINK_NOT_SET_UP, rank: rank('text') });
                        } else if (hasBookingLinkToken(inline.textSpa)) {
                            if (problems.some(p => p.key === key && p.field === 'spanish')) return;
                            problems.push({ key, field: 'spanish', scrollField: 'textSpa', message: `${label} (Spanish version): ${LINK_NOT_SET_UP}`, boxMessage: '', rank: rank('spanish') });
                        }
                    });
                }
                // Spanish the office typed is held to the same rule (patients would see the braces). Spanish the server
                // made is not: the office cannot fix it, and "Re-translate from English" is how they redo it.
                if (this.spanishEnabled === 'TRUE') {
                    entries.forEach(({ key, label, channel, inline, allowsLink, rank }) => {
                        if (problems.some(p => p.key === key)) return;
                        if (inline.spaEdited !== true) return;
                        const unknown = findUnknownVariables(inline.textSpa, { allowBookingLink: allowsLink });
                        const unknownSubject = channel === 'email' ? findUnknownVariables(inline.subjectSpa) : [];
                        if (!unknown.length && !unknownSubject.length) return;
                        const name = (unknown.length ? unknown[0] : unknownSubject[0]).name;
                        // The box already says it under the Spanish field (and opens itself), so no outline on the English.
                        problems.push({ key, field: 'spanish', message: `${label}: the Spanish version has {{${name}}}, which isn’t a variable we can fill in.`, boxMessage: '', rank: rank('spanish') });
                    });
                }
            }
            if (!problems.length) return true;

            // Page order (see above); the sort is stable, so the layer's own order holds within a box.
            problems.sort((a, b) => a.rank - b.rank);
            const marked = {};
            const markedSubjects = {};
            problems.forEach((p) => {
                if (!p.key || !this.slotIsBox(p.key)) return;
                // (a Spanish problem marks the box with '' - no outline - and never wipes a real message the English field has)
                if (p.field === 'subject') markedSubjects[p.key] = markedSubjects[p.key] || p.boxMessage;
                else marked[p.key] = marked[p.key] || p.boxMessage;
            });
            this.slotErrors = marked;
            this.slotSubjectErrors = markedSubjects;
            this.setErrored2(true);
            this.setMessage2(problems[0].message);
            this.$nextTick(() => this.scrollToBox(problems[0].key, problems[0].scrollField || problems[0].field));
            return false;
        },

        // `field` lands the cursor: 'subject' = the email box's Subject input instead of the message; 'subjectSpa' = the
        // Spanish subject; 'textSpa' / 'spanish' = the Spanish message (the Spanish section opens). Anything else = the message.
        scrollToBox(key, field) {
            let box = this.$refs['box-' + key];
            if (Array.isArray(box)) box = box[0]; // a ref inside v-for (the recall-type rule boxes) is a list
            if (!box || !box.$el) return;
            if (typeof box.$el.scrollIntoView === 'function') box.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const which = { subject: 'subject', subjectSpa: 'spanish-subject', textSpa: 'spanish', spanish: 'spanish' }[field];
            if (typeof box.focus === 'function') box.focus(which);
        },

        // The Super Admin "New view" / "Classic view" link (an office user's switch reloads the page instead).
        setMessageLayout(target) {
            const how = switchUi('reminderMessages', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID });
            if (how === 'rerender') {
                this.layoutVersion += 1;
                this._hydrateInlineSlots();
                this._loadBookingTypes(); // the boxes just appeared: the booking-link picker's appointment types
            }
        },

        async pause() {
            this.hideModal('pause');
            this.activateAptReminderOn = '';
            let settingsToSave = this._buildSettingsObject();

            await this.pauseAppointmentReminder({siteID: this.siteID, settings: JSON.stringify(settingsToSave)});

            this.allSettings.APPT_REMINDER_ACTIVATED.PropertyValue = 'FALSE';
        },

        onRecallReminderMaxNumChange() {
            if(this.recallReminderMaxNum !== "") {
                console.log(`Recall reminder max num changed to ${this.recallReminderMaxNum}`);
                this.recallReminderFollowUps = [];            
                if(this.recallReminderMaxNum>1) {
                    for(let i = 1; i <= this.recallReminderMaxNum; i++) {
                        let temp = 'RECALL_REMINDER_FOLLOWUP_' + i;                    
                        this.recallReminderFollowUps.push({
                            recallType: 'Text',
                            duration: 1,
                            durationType : 'days'
                            //duration: this._settings[temp] ? this._settings[temp].PropertyValue.split(' ')[0] : 1,
                            //durationType: this._settings[temp] ? this._settings[temp].PropertyValue.split(' ')[1] : 'days'
                        })                    
                    }
                } else {
                    this.recallReminderFollowUps=[{
                        recallType: 'Text',
                        durationType: 'days',
                        duration: 1
                    }]
                }
            }
        },

        removeFollowup(index){
            //console.log(index);
            if(this.recallReminderFollowUps.length > 0) {
                this.recallReminderFollowUps.splice(index,1);
                this.recallReminderMaxNum--;
            }
        },

        providerCustomLabel({FName, MI, LName, Suffix, Abbr}) {
            return `${FName} ${MI} ${LName} ${Suffix} (${Abbr})`;
        },

        removeProvider(option) {
            let removeIdx = this.selectedProvider.findIndex(each => {
                return each.ProvNum === option.ProvNum;
            });
            this.selectedProvider.splice(removeIdx, 1);
        },

        addRecallTypeRule() {
            if (!this.useInlineMessages) {
                this.recallReminderTypeRules.push({
                    recallTypes: [],
                    smsTemplate: null,
                    emailTemplate: null
                });
                return;
            }
            // The new screen: a rule is born with its id and both channels as plain messages holding the defaults.
            const id = this._newRuleId();
            this.recallReminderTypeRules.push({
                id,
                recallTypes: [],
                smsTemplate: buildInlineRuleTemplate(id, 'sms', { text: this._pickDefault(getRuleDefault('sms')) }),
                emailTemplate: buildInlineRuleTemplate(id, 'email', { text: this._pickDefault(getRuleDefault('email')) })
            });
        },

        removeRecallTypeRule(index) {
            this.recallReminderTypeRules.splice(index, 1);
        },

        // ── Recall-type rules as cards (inline reminder messages WP6) ────────────────────────────────────────
        // A rule is { id?, recallTypes, smsTemplate, emailTemplate }. Each channel is a plain message ('inline'), a
        // wiki page ('wiki', today's dropdown) or off (null). The id ([a-z0-9]{8}, made here, unique among the
        // rules) is what gives each plain message its own template id 'inline:rule:<id>:sms|email', so the cloud can
        // tell the rules apart by equality of that id.

        // 'rule:<id>:<channel>', the key every message helper takes; '' when the rule has no usable id yet.
        ruleKeyOf(rule, channel) {
            return rule && typeof rule === 'object' && inlineRuleId(rule.id, channel) ? `rule:${rule.id}:${channel}` : '';
        },

        // Rule messages follow the recall switch.
        ruleSectionOn() {
            return String(this[RULE_SECTION]) === 'TRUE';
        },

        // 'inline' (a plain message, drawn as a box), 'wiki' (a picked page, the dropdown) or 'off'. A plain message on
        // a rule with no usable id has no key to be edited by, so it is not drawn as a box.
        ruleState(rule, channel) {
            const tpl = rule && typeof rule === 'object' ? rule[RULE_TPL_PROP[channel]] : null;
            if (!tpl || typeof tpl !== 'object' || !tpl.WikiPageID) return 'off';
            return isInlineRuleTemplate(tpl) && this.ruleKeyOf(rule, channel) ? 'inline' : 'wiki';
        },

        // 8 characters of [a-z0-9] that no rule has yet. Bounded: a stuck random source ends in an error, not a loop.
        _newRuleId() {
            const taken = new Set((Array.isArray(this.recallReminderTypeRules) ? this.recallReminderTypeRules : []).map(r => r && r.id).filter(Boolean));
            const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
            for (let attempt = 0; attempt < 100; attempt += 1) {
                let id = '';
                for (let i = 0; i < 8; i += 1) id += chars.charAt(Math.floor(Math.random() * chars.length));
                if (!taken.has(id)) return id;
            }
            throw new Error('Could not make a unique recall rule id');
        },

        // A rule saved before ids existed (or with a bad one) gets one when a channel is switched to a plain message.
        _ensureRuleId(rule) {
            if (!inlineRuleId(rule.id, 'sms')) Vue.set(rule, 'id', this._newRuleId());
            return rule.id;
        },

        // Everything the rule's box needs for one channel (the on-screen wording mirrors the slot boxes).
        ruleBoxProps(index, channel) {
            const rule = this.recallReminderTypeRules[index];
            const key = this.ruleKeyOf(rule, channel);
            const def = this._defaultAt(key) || {};
            const isEmail = channel === 'email';
            const props = {
                label: isEmail ? 'Email for these recall types:' : 'Text message for these recall types:',
                slotKey: key,
                channel,
                value: this.inlineTextOf(key),
                defaultText: this._pickDefault(def),
                chips: def.chips ? def.chips.slice() : [],
                helper: def.helper || '',
                error: this.slotErrors[key] || '',
                note: this.slotNotes[key] || '',
                // Recall off ⇒ read-only. WhatsApp-only office ⇒ the TEXT is not what is sent, so that box is for reference.
                disabled: !this.sectionOn(key),
                informational: !isEmail && !!this.smsOutboundOff,
                // "Use a wiki template instead" only when there is a page to go back to.
                wikiLink: !!this.wikiStash[key],
                spanish: this.spanishFor(key),
                booking: this.bookingProps(key),
            };
            if (isEmail) {
                props.subject = this.inlineSubjectOf(key);
                props.defaultSubject = def.subject || '';
                props.subjectError = this.slotSubjectErrors[key] || '';
                props.previewSite = this.emailPreviewSite;
            }
            return props;
        },

        // "Switch to a plain message" on a rule channel: a wiki page, the gate on, recall on, and (text) an office that texts.
        canSwitchRuleToPlain(index, channel) {
            const rule = Array.isArray(this.recallReminderTypeRules) ? this.recallReminderTypeRules[index] : null;
            return !!rule && this.ruleState(rule, channel) === 'wiki'
                && this.useInlineMessages && this.ruleSectionOn() && (channel === 'email' || !this.smsOutboundOff);
        },

        // Wiki page → plain message for one rule channel (the same read as a slot's). Resolves the message key.
        async switchRuleToPlain(index, channel) {
            const rule = this.recallReminderTypeRules[index];
            if (!rule) return '';
            this._ensureRuleId(rule);
            const key = this.ruleKeyOf(rule, channel);
            await this.switchToPlain(key);
            return key;
        },

        // "Don't send a text / an email for these recall types": the channel goes off (null). The message (or the
        // wiki page) is kept for "Add a text message" / "Use a wiki template instead" to bring back.
        turnRuleChannelOff(index, channel) {
            const rule = this.recallReminderTypeRules[index];
            if (!rule) return;
            const prop = RULE_TPL_PROP[channel];
            const key = this.ruleKeyOf(rule, channel);
            const tpl = rule[prop];
            if (key && tpl) {
                if (isInlineRuleTemplate(tpl)) this.plainStash = { ...this.plainStash, [key]: tpl };
                else this.wikiStash = { ...this.wikiStash, [key]: tpl };
                this.slotNotes = { ...this.slotNotes, [key]: '' };
                if (this.slotErrors[key]) {
                    const { [key]: _gone, ...rest } = this.slotErrors;
                    this.slotErrors = rest;
                }
                if (this.slotSubjectErrors[key]) {
                    const { [key]: _gone, ...rest } = this.slotSubjectErrors;
                    this.slotSubjectErrors = rest;
                }
            }
            Vue.set(rule, prop, null);
        },

        // "Add a text message / an email" on an off channel: the message that was just turned off if there is one, else
        // the rule default. The rule gets an id if it has none.
        addRuleChannel(index, channel) {
            const rule = this.recallReminderTypeRules[index];
            if (!rule) return;
            const id = this._ensureRuleId(rule);
            const key = this.ruleKeyOf(rule, channel);
            const back = this.plainStash[key];
            Vue.set(rule, RULE_TPL_PROP[channel], isInlineRuleTemplate(back) ? back : buildInlineRuleTemplate(id, channel, { text: this._pickDefault(getRuleDefault(channel)) }));
            this.slotNotes = { ...this.slotNotes, [key]: '' };
        },

        // ── "Switch every message to a plain message" ───────────────────────────────────────────────────────
        // Is this slot on screen right now? (The general recall boxes are replaced by the rules in "Specific
        // Recall Type"; the booked boxes only exist while their channel is ticked.)
        _slotIsShown(key) {
            const def = getSlotDefault(key) || {};
            if (def.family === 'recall-reminder') return this.recallReminderMode !== 'SPECIFIC_TYPE';
            if (key === 'apptBookedSmsTpl') return this.apptBookedConfirmation === 'TRUE' && !!this.apptBookedSms;
            if (key === 'apptBookedEmailTpl') return this.apptBookedConfirmation === 'TRUE' && !!this.apptBookedEmail;
            return true;
        },

        // The wiki messages the per-message "Switch to a plain message" link is offered on, in page order: the slots,
        // then the rules' channels. One job each: { key } for a slot, { index, channel } for a rule.
        wikiMessageJobs() {
            if (!this.useInlineMessages) return [];
            const jobs = [];
            INLINE_KEYS.forEach(key => {
                const slot = this[key];
                if (!slot || typeof slot !== 'object' || !slot.WikiPageID || isInlineSlot(slot)) return;
                if (this._slotIsShown(key) && this.canSwitchToPlain(key)) jobs.push({ key });
            });
            if (this.recallReminderMode === 'SPECIFIC_TYPE') {
                (Array.isArray(this.recallReminderTypeRules) ? this.recallReminderTypeRules : []).forEach((_rule, index) => {
                    ['sms', 'email'].forEach(channel => {
                        if (this.canSwitchRuleToPlain(index, channel)) jobs.push({ index, channel });
                    });
                });
            }
            return jobs;
        },

        // Runs the existing per-message switch on every wiki message, one after another (each reads its page; no
        // burst of requests). Nothing is saved — the office reviews the boxes, then presses Save.
        async switchEverythingToPlain() {
            if (this.convertBusy) return;
            const jobs = this.wikiMessageJobs();
            if (!jobs.length) return;
            this.convertBusy = true;
            this.convertResult = null;
            let switched = 0;
            let defaulted = 0;
            try {
                for (const job of jobs) {
                    let key;
                    if (job.key !== undefined) {
                        key = job.key;
                        await this.switchToPlain(key);
                    } else {
                        key = await this.switchRuleToPlain(job.index, job.channel);
                    }
                    if (!key || !this._isInlineAt(key)) continue;
                    switched += 1;
                    if (this.slotNotes[key]) defaulted += 1; // the page could not be read: it started from the default
                }
            } finally {
                this.convertBusy = false;
            }
            this.convertResult = { switched, defaulted };
        },

        convertSummary(result) {
            if (!result) return '';
            const n = result.switched;
            let text = `Switched ${n} message${n === 1 ? '' : 's'} to plain text — review them, then Save.`;
            if (result.defaulted > 0) text += ` ${result.defaulted} couldn’t be read and started from the default message.`;
            return text;
        },
    },

    computed: {
        // Recall attempt-1 is owned by the cloud leg when the operator flipped this site to
        // 'BACKEND' (appt-reminder-service _recallAttempt1Owner). Absent/anything else = the exe.
        /*
            What the "Send Auto Recall Reminders" switch reads and writes.

            MIRRORS appt-reminder-service lib/recall-attempt1.js resolveRecallEnabled(). Once a site's
            exe leg is retired, `recallReminders` becomes the EXE'S MUTE — 'FALSE' on a perfectly
            healthy cloud-sending practice — so the switch must stop binding it directly or it would
            read OFF while patients were being texted. `recallRemindersEnabled` carries the intent
            from then on, and the SETTER writes whichever key actually governs this site.

            ⛔ On a migrated site the setter must NEVER switch recallReminders back on: that key is the
            only lever the deployed exe honours, and re-enabling it would un-retire the exe leg and
            put both senders back in the race.

            ⛔ It MUST mute it when the office switches recall OFF. Until a migrated site's exe leg is
            retired the exe is still sending, and it reads nothing else — writing only
            recallRemindersEnabled stopped the cloud and left the exe texting patients (prod
            2026-09-13, Mint + Allay, caught before any office used it). Muting is always the safe
            direction. So OFF then ON on a not-yet-retired site ends RETIRED: cloud sends, exe quiet.

            Absent ⇒ falls back, so every site that predates the key behaves exactly as before.
        */
        recallRemindersOn: {
            get() {
                if (!this.recallSentByCloud) return this.recallReminders;
                const raw = this.recallRemindersEnabled;
                if (raw === undefined || raw === null || String(raw).trim() === '') return this.recallReminders;
                return String(raw).trim().toUpperCase() === 'TRUE' ? 'TRUE' : 'FALSE';
            },
            set(v) {
                const next = String(v).trim().toUpperCase() === 'TRUE' ? 'TRUE' : 'FALSE';
                if (!this.recallSentByCloud) {
                    this.recallReminders = next;
                    return;
                }
                this.recallRemindersEnabled = next;
                if (next === 'FALSE') this.recallReminders = 'FALSE';
            },
        },

        recallSentByCloud() {
            return String(this.recallSendLeg || 'EXE').trim().toUpperCase() === 'BACKEND';
        },
        allSettings() {
         if(this.settings)
            return _.keyBy(this.settings, 'Property');
        },

        operatories() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[0];
            }
        },

        doNotSendAptConfirmationStatuses() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[1];
            }
        },

        // Channel-typed apt-reminder template lists. An SMS slot must only offer SMS templates
        // and an email slot only email templates — picking the wrong type is what broke Tulip
        // Tree (2026-06-29): an SMS template, whose SendgridTemplateID is the literal string
        // 'undefined', was selected in an email confirmation slot → the email send hit SendGrid
        // with template_id 'undefined' → 400. These replace the old untyped `aptReminderTemplates`
        // and mirror the apt-booked/recall filters below. KEEP IN SYNC with
        // cypress/node-tests/apt-reminder-template-filters.test.js
        aptReminderSmsTemplates() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[3].filter(o =>
                    o.Tags && o.Tags.includes("apt-reminder") && o.Tags.includes("sms-template")
                );
            }
            return [];
        },

        aptReminderEmailTemplates() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[3].filter(o =>
                    o.Tags && o.Tags.includes("apt-reminder") && o.Tags.includes("email-template")
                );
            }
            return [];
        },

        recallReminderTempaltes() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[3].filter(o => o.Tags && o.Tags.includes("recall-reminder"));
            }
        },

        recallReminderSmsTempaltes() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[3].filter(o =>
                    o.Tags && o.Tags.includes("recall-reminder") && o.Tags.includes("sms-template")
                );
            }
            return [];
        },

        recallReminderEmailTempaltes() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[3].filter(o =>
                    o.Tags && o.Tags.includes("recall-reminder") && o.Tags.includes("email-template")
                );
            }
            return [];
        },

        apptBookedSmsTemplates() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[3].filter(o =>
                    o.Tags && o.Tags.includes("apt-booked") && o.Tags.includes("sms-template")
                );
            }
            return [];
        },

        apptBookedEmailTemplates() {
            if(this.allAptReminderSettings) {
                return this.allAptReminderSettings[3].filter(o =>
                    o.Tags && o.Tags.includes("apt-booked") && o.Tags.includes("email-template")
                );
            }
            return [];
        },

        recallTypeOptions() {
            if(this.allAptReminderSettings && this.allAptReminderSettings[4])
                return this.allAptReminderSettings[4];
            return [];
        },

        // How every visible provider appears in {{apt_provider}} (WP7): the server's res[5], already computed with the layer rule
        // teamio-utility uses for the send, so this screen never disagrees with what patients get. res[5] is absent from an older
        // backend (or when the server could not build it) - then there is nothing to show.
        providerNameRows() {
            return providerRowsForDisplay(this.allAptReminderSettings && this.allAptReminderSettings[5]);
        },

        // Same gate as the plain message boxes (the card is part of that release), and only when the server sent providers.
        showProviderNames() {
            return !!this.useInlineMessages && this.providerNameRows.length > 0;
        },

        providerHint() {
            return PROVIDER_HINT;
        },
        
        aptReminderActivated() {
            if(this.allSettings) {
                return this.allSettings.APPT_REMINDER_ACTIVATED ? this.allSettings.APPT_REMINDER_ACTIVATED.PropertyValue === 'TRUE' ? true : false  : false;
            }
        },

        aptReminderStatus() {
            if(this.allSettings) {
                return this.allSettings.APPT_REMINDER_ACTIVATED ? this.allSettings.APPT_REMINDER_ACTIVATED.PropertyValue === 'TRUE' ? 'ON' : 'OFF'  : 'OFF';
            }
        },

        // Site capability — src/utils/siteCapability.js (single implementation).
        // The `if (this.allSettings)` wrapper is preserved: these return undefined (not
        // false) while settings load, and callers depend on that tri-state.
        // emailVerified additionally gains the real five-shape parse — it used to test
        // `PropertyValue !== ''`, which called "[]" a verified sender.
        emailVerified() {
            if(this.allSettings) {
                return isEmailEnabled(this.allSettings);
            }
        },

        // Text CONFIRMATIONS / REMINDERS: hasSmsNumber, not isSmsEnabled. At a WhatsApp-only office
        // (SMS_OUTBOUND_DISABLED) teamio-utility sends these as approved WhatsApp templates
        // (utility.js "WhatsApp reminder branch"), so turning them off here would silence the
        // office's WhatsApp reminders too. A reminder type with no WhatsApp template is not sent.
        phoneAvailable() {
            if(this.allSettings) {
                return hasSmsNumber(this.allSettings);
            }
        },

        // Appointment-BOOKED confirmation by SMS goes through the plain SMS path (no WhatsApp
        // branch), so it can never fire at a WhatsApp-only office — refuse to save it there.
        bookedSmsAvailable() {
            return isSmsEnabled(this.allSettings);
        },

        smsOutboundOff() {
            return hasSmsNumber(this.allSettings) && isSmsOutboundDisabled(this.allSettings);
        },

        smsDisabledMessage() {
            return SMS_DISABLED_MESSAGE;
        },

        // Does this office have Online Scheduling (ONLINE_SCHEDULING_PAID_FOR in the site settings the app already loads)?
        // Decides whether the recall messages offer the online-scheduling link, which default text they start from, and
        // whether a box that holds the link may be saved.
        hasOnlineScheduling() {
            return isOnlineSchedulingPaid(this.allSettings);
        },

        // What the Text Confirmations section tells a WhatsApp-only office — honest about whether
        // WhatsApp can actually deliver these (the save refuses when it can't).
        textWhatsAppNote() {
            return whatsAppReminderCoverage(this.allSettings).appointment
                ? 'Texting is turned off for this office, so text confirmations and reminders go out on WhatsApp using your approved WhatsApp template.'
                : 'Texting is turned off for this office and there is no WhatsApp template for appointment reminders yet, so text confirmations and reminders cannot be sent. Contact Teamio to add one.';
        },

        // WhatsApp-only office (smsOutboundOff: the SAME predicate whatsappReminderCoverage.js uses)
        // where WhatsApp can deliver appointment reminders: the text templates below are not what
        // goes out, so say which message does. Anywhere else the note would be false.
        showWhatsAppTemplateNote() {
            return this.smsOutboundOff && whatsAppReminderCoverage(this.allSettings).appointment;
        },

        recallWhatsAppWarning() {
            return recallTextWarning(this.allSettings);
        },

        // The release gate for the plain message boxes (src/utils/followups/uiVersion.js, area 'reminderMessages').
        // false ⇒ the screen is exactly the classic one: every SMS slot is the wiki dropdown, and a plain message
        // that is already stored just shows as "Plain message" in it and is saved back untouched. true ⇒ the 11 SMS
        // slots that hold a plain message (or are empty) are message boxes. userKey (the employee ID) is the
        // office's own "classic Teamio" choice, once the area is past 'preview'.
        useInlineMessages() {
            // eslint-disable-next-line no-unused-expressions
            this.layoutVersion;
            return shouldUseNewFollowupsUi('reminderMessages', {
                isSuperAdmin: this.isSuperAdmin,
                storage: browserStorage(),
                userKey: this.userID,
            });
        },

        // The Super Admin wording on the classic <-> boxes link (an office user always reads the office wording).
        messageLayoutNote() {
            return this.useInlineMessages
                ? 'Super Admin preview — offices still see the template dropdowns.'
                : 'Super Admin: plain message boxes for the text and email templates are ready to check.';
        },

        // The practice the email boxes' preview is drawn for: the customer's own site row (name, logo, address, phone —
        // the same fields the email layout's header and footer read from teamio.sites). Missing parts fall back to
        // the sample practice inside the box.
        // The wiki messages "Switch every message to a plain message" would switch right now (see wikiMessageJobs).
        convertJobs() {
            return this.wikiMessageJobs();
        },

        // Does the sender append an email signature at this office? The sender's own test (utility.js
        // readEmailSignatureHtml): the EMAIL_SIGNATURE setting parses, is switched on, and holds non-blank html. It is
        // already in the store with the rest of customer.settings, so no call is made. With a signature the sent email
        // carries NO logo header and NO footer (the signature is the branding), and the preview draws it that way.
        // Settings not loaded yet => undefined: the box then reads it as "has one" (most offices do).
        hasEmailSignature() {
            if (!this.settings) return undefined;
            return isSignatureConfigured(parseSignatureSetting(readSettingValue(this.settings, 'EMAIL_SIGNATURE')));
        },

        emailPreviewSite() {
            const site = this.getSite(this.siteID) || {};
            const cityState = [site.City, site.State].filter(Boolean).join(', ');
            const address = [site.Address1, [cityState, site.Zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
            return {
                name: site.Name || '',
                logoUrl: site.LogoUrl || '',
                address,
                phone: site.OfficePhone || '',
                hasSignature: this.hasEmailSignature,
            };
        }
    }
}

</script>
<style lang='scss' scoped>

// Cancellation alerts (patient clicked "I need to Cancel" in a reminder email).
.decline-alerts {
    clear: both;
    margin-top: 15px;

    .decline-emails {
        margin: 8px 0 0 24px;

        .form-control {
            max-width: 420px;
        }

        small {
            display: block;
            margin-top: 4px;
        }
    }
}

.minor-panel{
    .form-control{
        min-width: 80px;
        width:100px;
        margin-left: 10px;
        display: inline-block;
    }
}

.spanish-settings-panel {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 15px;
    background: linear-gradient(135deg, #f0f8ff 0%, #fff 100%);
    
    .spanish-header {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        
        .spanish-icon {
            font-size: 1.3rem;
            margin-right: 10px;
        }
    }
    
    .spanish-content {
        padding-left: 5px;
    }
}

.info-section{
    border-bottom: 1px solid #d6dee5;
    padding-top: 25px;
    padding-bottom: 25px;
    .custom-select{
        width: auto;
        height:34px !important;
        margin: 0 5px;
        min-width: 100px;
    }
}

.settingsDisabled {
    background-color: #cccccc;
    color: #666666;
}

.multiselect {
    max-width: 450px;
}

// "Wiki page template · Switch to a plain message" under a wiki dropdown (plain message boxes, Super Admin preview).
.plain-switch {
    margin: 8px 0 0;
    font-size: 13px;
    color: #717977;

    .plain-switch__btn {
        all: unset;
        cursor: pointer;
        font-family: inherit;
        font-weight: 600;
        color: #0f7259;

        &:hover {
            color: #0b5c49;
            text-decoration: underline;
        }

        &:focus-visible {
            outline: 2px solid #0f7259;
            outline-offset: 2px;
        }

        &:disabled {
            opacity: 0.6;
            cursor: progress;
        }
    }
}

// Links under a recall-type rule's channel ("Switch to a plain message", "Don't send a text…", "Add a text message").
.rule-links,
.rule-off {
    margin: 8px 0 0;
    font-size: 13px;
    color: #717977;
}

.rule-links__btn {
    all: unset;
    cursor: pointer;
    font-family: inherit;
    font-weight: 600;
    color: #0f7259;

    &:hover {
        color: #0b5c49;
        text-decoration: underline;
    }

    &:focus-visible {
        outline: 2px solid #0f7259;
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.6;
        cursor: progress;
    }
}

// Recall-type rules as cards (plain message boxes view): the rule's recall types, then a text message and an email.
.rule-cards {
    max-width: 760px;

    .rule-card {
        margin-top: 14px;
        padding: 16px 18px 18px;
        border: 1px solid #d6dee5;
        border-radius: 10px;
        background: #fff;

        // Recall reminders are switched off: the card stays readable but is visibly inactive.
        &.is-off {
            background: #f7f9fa;
        }
    }

    .rule-card__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .rule-card__title {
        font-weight: 600;
    }

    .rule-card__label {
        margin: 10px 0 4px;
    }

    .rule-channel {
        margin-top: 16px;
        padding-top: 14px;
        border-top: 1px dashed #e2e8ec;
    }
}

// "Switch every message to a plain message": one line at the top of the screen.
.convert-all {
    margin: 4px 0 12px;

    .convert-all__row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 14px;
        padding: 10px 14px;
        border: 1px solid #cfe4dd;
        border-radius: 8px;
        background: #f2f9f6;
    }

    .convert-all__btn {
        all: unset;
        display: inline-block;
        cursor: pointer;
        padding: 6px 12px;
        border: 1px solid #0f7259;
        border-radius: 6px;
        font-family: inherit;
        font-size: 13px;
        font-weight: 600;
        color: #0f7259;
        background: #fff;

        &:hover {
            background: #e7f3ef;
        }

        &:focus-visible {
            outline: 2px solid #0f7259;
            outline-offset: 2px;
        }

        &:disabled {
            opacity: 0.6;
            cursor: progress;
        }
    }

    .convert-all__hint {
        font-size: 13px;
        color: #717977;
    }

    .convert-all__result {
        margin: 8px 0 0;
        font-size: 13px;
        font-weight: 600;
        color: #0b5c49;
    }
}

// "How your providers appear in messages": one line per provider, a long list scrolls inside the card.
.provider-names {
    max-width: 760px;
    padding: 14px 16px;
    border: 1px solid #d6dee5;
    border-radius: 10px;
    background: #fff;

    code {
        color: #0b5c49;
    }

    .provider-names__list {
        margin: 0 0 10px;
        padding: 0;
        max-height: 280px;
        overflow-y: auto;
        list-style: none;
    }

    .provider-names__row {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 2px 10px;
        padding: 7px 0;
        border-top: 1px solid #eef2f4;

        &:first-child {
            border-top: 0;
        }
    }

    .provider-names__abbr {
        min-width: 90px;
        font-weight: 600;
        color: #1f2d2a;
    }

    .provider-names__arrow {
        color: #717977;
    }

    .provider-names__appears {
        font-weight: 600;
        color: #0b5c49;
    }

    .provider-names__row--left-out .provider-names__appears {
        font-weight: 400;
        color: #717977;
    }

    .provider-names__meta {
        display: inline-flex;
        flex-wrap: wrap;
        gap: 0 10px;
        font-size: 12px;
        color: #717977;
    }
}

.recall-leg-badge {
    clear: both;
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-left: 3px solid #0f7259;
    background: rgba(15, 114, 89, 0.06);
    font-size: 0.9rem;
}
</style>